import { calculateOverdueDays } from '@/utils/common'
import dayjs from 'dayjs'

const parseVal = (val) => {
  if (val === null || val === undefined) return 0
  if (typeof val === 'number') return val
  if (typeof val === 'object' && val.$numberDecimal !== undefined) {
    return parseFloat(val.$numberDecimal) || 0
  }
  const parsed = parseFloat(val)
  return isNaN(parsed) ? 0 : parsed
}

const isPaidEmi = (emi) => {
  if (!emi) return false
  const status = String(emi.emiStatus || '').trim()
  return (
    emi.isPaid === true ||
    emi.isPaid === 'true' ||
    ['Paid', 'Late', 'Late Paid', 'Minimum Amount Paid'].includes(status)
  )
}

function getOverdueDays(emi) {
  if (!emi?.dueDate) return 0
  const status = (emi.emiStatus || '').toLowerCase()
  if (status !== 'pending' && status !== 'overdue') return 0
  const diff = dayjs().startOf('day').diff(dayjs(emi.dueDate).startOf('day'), 'day')
  return diff > 0 ? diff : 0
}

const getTeamName = (loan, emi, teams) => {
  const teamVal = loan?.teamName || loan?.team || loan?.centreName || loan?.loanDetails?.teamName || loan?.loanDetails?.team || loan?.loanDetails?.centreName || emi?.case?.team || emi?.loan?.team;
  if (typeof teamVal === 'object' && teamVal !== null) return teamVal.teamName || teamVal.centreName || '-';
  if (teamVal && typeof teamVal === 'string' && teams && Array.isArray(teams)) {
    const matchedTeam = teams.find((t) => t._id === teamVal || t.name === teamVal);
    if (matchedTeam) return matchedTeam.teamName || matchedTeam.name || '-';
  }
  return teamVal || '-';
}

const getBranchName = (loan, emi, branches) => {
  const branchVal = loan?.branchName || loan?.branch || loan?.loanDetails?.branchName || loan?.loanDetails?.branch || emi?.case?.branch || emi?.loan?.branch;
  if (typeof branchVal === 'object' && branchVal !== null) return branchVal.branchName || '-';
  if (branchVal && typeof branchVal === 'string' && branches && Array.isArray(branches)) {
    const matchedBranch = branches.find((b) => b._id === branchVal || b.name === branchVal);
    if (matchedBranch) return matchedBranch.branchName || matchedBranch.name || '-';
  }
  return branchVal || '-';
}

export const getAssetInfo = (loan, assetClassification) => {
  if (!assetClassification || !Array.isArray(assetClassification)) return null

  // Calculate DPD
  const now = dayjs()
  const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
  const overdueEmis = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(now))

  let currentDpd = 0
  if (overdueEmis.length > 0) {
    const oldestOverdue = overdueEmis.sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
    currentDpd = now.diff(dayjs(oldestOverdue.dueDate), 'day')
  }

  // Calculate Principal Outstanding (POS)
  let pos = 0
  if (schedule.length > 0) {
    pos = schedule
      .filter((e) => !isPaidEmi(e))
      .reduce((sum, e) => sum + (Number(e.principalAmount) || 0), 0)
  } else {
    pos = Number(loan?.loanDetails?.outstandingPrincipal || loan?.outstandingAmount || 0)
  }

  // Find matching classification bucket
  const classification = assetClassification.find((c) => {
    const minDpd = Number(c.minDpd)
    const maxDpd = Number(c.maxDpd)
    const hasMax = c.maxDpd !== undefined && c.maxDpd !== null && c.maxDpd !== ''
    return currentDpd >= minDpd && (!hasMax || currentDpd <= maxDpd)
  })

  if (classification) {
    const percentage = Number(classification.provisioningPercentage) || 0
    return {
      category: classification.category,
      provisioningPercentage: percentage,
      provisioningAmount: (pos * percentage) / 100,
      principalOutstanding: pos,
      dpd: currentDpd
    }
  }
  return null
}

export const COLUMN_MAPPERS = {
  asset_category: ({ loan }) => loan?.assetInfo?.category || '-',
  provisioning_amount: ({ loan }) => parseVal(loan?.assetInfo?.provisioningAmount).toFixed(2),
  loan_account_no: ({ loan }) =>
    loan?.applicationNumber || loan?.caseNo || loan?.loanDetails?.applicationNumber || '-',
  customer_name: ({ loan, emi }) =>
    emi?.customer?.customerFullName || loan?.customer?.customerFullName || '-',
  amount_collected: ({ loan, emi, payment }) => {
    if (payment?.amount) return parseVal(payment.amount).toFixed(2)
    if (emi?.emiAmount) return parseVal(emi.emiAmount).toFixed(2)
    // If loan-level aggregation
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const total = schedule
      .filter((e) => isPaidEmi(e))
      .reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total.toFixed(2)
  },
  collection_date: ({ emi, payment, loan }) => {
    const date = payment?.date || emi?.paymentDate || loan?.lastPaymentDate
    return date ? dayjs(date).format('DD/MM/YYYY') : '-'
  },
  agent_name: ({ loan, rowData }) => {
    if (rowData?.agent_name) return rowData.agent_name
    if (loan?.assignedTo && Array.isArray(loan.assignedTo) && loan.assignedTo.length > 0) {
      if (typeof loan.assignedTo[0] === 'object') {
        return loan.assignedTo.map((u) => `${u.firstName} ${u.lastName || ''}`).join(', ')
      }
    }
    return loan?.createdBy ? `${loan.createdBy.firstName} ${loan.createdBy.lastName || ''}` : 'N/A'
  },
  interest_rate: ({ loan }) => {
    const rate = parseFloat(loan?.loanDetails?.interestRate || 0)
    const monthly = rate / 12
    return `${monthly.toFixed(2)}(M) ${rate.toFixed(2)}(Y)`
  },
  tenure_count: ({ loan }) => loan?.tenure || loan?.loanDetails?.tenure || '-',
  sanction_date: ({ loan }) => {
    const date =
      loan?.caseStartDate ||
      loan?.loanDetails?.emiStartDate ||
      loan?.loanDetails?.createdAt ||
      loan?.createdAt
    return date ? dayjs(date).format('DD-MM-YYYY') : '-'
  },
  amount_sanctioned_repeat: ({ loan }) =>
    parseVal(loan?.loanDetails?.loanAmountSanctioned || loan?.loanAmountSanctioned).toFixed(2),
  payment_mode: ({ loan, payment }) =>
    payment?.paymentMode || loan?.loanDetails?.loanDisbursements?.[0]?.paymentMode || 'N/A',
  receipt_no: ({ emi, payment }) => payment?.voucherNo || emi?.voucherNo || emi?._id || '-',
  emi_amount_due: ({ emi }) => parseVal(emi?.emiAmount).toFixed(2),
  due_date: ({ loan, emi }) => {
    if (emi?.dueDate) return dayjs(emi.dueDate).format('DD/MM/YYYY')
    const emiSchedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    if (emiSchedule.length)
      return dayjs(emiSchedule[emiSchedule.length - 1].dueDate).format('DD/MM/YYYY')
    return '-'
  },
  pending_days: ({ loan, emi }) => {
    let targetEmi = emi
    if (!targetEmi?.dueDate) {
      const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
      let oldestDpd = 0
      let oldestEmi = null
      schedule.forEach((e) => {
        const dpd = getOverdueDays(e)
        if (dpd > oldestDpd) {
          oldestDpd = dpd
          oldestEmi = e
        }
      })
      targetEmi = oldestEmi
    }

    if (!targetEmi) return 0
    return getOverdueDays(targetEmi)
  },
  customer_contact: ({ loan, emi }) => emi?.customer?.crn || loan?.customer?.crn || '-',
  total_principal_due: ({ emi }) => parseVal(emi?.principalAmount).toFixed(2),
  total_interest_due: ({ emi }) => parseVal(emi?.interestAmount).toFixed(2),
  overdue_charges: ({ emi }) => parseVal(emi?.penaltyAmount).toFixed(2),
  late_fee_amount: ({ emi, payment }) => {
    if (payment?.latePaymentAmount !== undefined) return parseVal(payment.latePaymentAmount).toFixed(2)
    const overdueInfo = calculateOverdueDays(
      emi?.dueDate,
      emi?.emiStatus,
      emi?.paymentDate,
      emi?.emiAmount,
      emi?.lateFeeType,
      emi?.lateFeeValue
    )
    return parseVal(overdueInfo?.lateFeeAmount).toFixed(2)
  },
  target_amount: ({ rowData }) => parseVal(rowData?.target_amount).toFixed(2),
  collected_amount: ({ rowData }) => parseVal(rowData?.collected_amount).toFixed(2),
  achievement_percent: ({ rowData }) => {
    const target = parseVal(rowData?.target_amount)
    const collected = parseVal(rowData?.collected_amount)
    return target ? ((collected / target) * 100).toFixed(2) + '%' : '0%'
  },
  date: ({ rowData }) => rowData?.date || '-',
  branch: ({ loan, emi, rowData }, branches, teams) => {
    if (rowData?.branch) return rowData.branch
    const branch = getBranchName(loan, emi, branches)
    return branch === '-' ? 'Salesforce' : branch
  },
  last_payment_date: ({ loan }) => {
    const emiSchedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const paid = emiSchedule.filter((e) => isPaidEmi(e)) || []
    if (paid.length === 0) return '-'
    const last = paid.sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
    return last?.paymentDate ? dayjs(last.paymentDate).format('DD/MM/YYYY') : '-'
  },
  bucket: ({ loan, emi }) => {
    let targetEmi = emi
    if (!targetEmi?.dueDate) {
      const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
      let oldestDpd = 0
      let oldestEmi = null
      schedule.forEach((e) => {
        const dpd = getOverdueDays(e)
        if (dpd > oldestDpd) {
          oldestDpd = dpd
          oldestEmi = e
        }
      })
      targetEmi = oldestEmi
    }

    if (!targetEmi) return 'Current'
    const days = getOverdueDays(targetEmi)
    if (days === 0) return 'Current'
    if (days <= 30) return '1-30'
    if (days <= 60) return '31-60'
    if (days <= 90) return '61-90'
    return '90+'
  },
  loan_type: ({ loan }) => loan?.typeOfLoan || '-',
  emi_no: ({ emi }) => emi?.emiNo || '-',
  emi_status: ({ loan, emi }) => {
    if (isPaidEmi(emi)) return emi?.emiStatus || 'Paid'
    if (loan) {
      const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
      if (schedule.length > 0 && schedule.every((e) => isPaidEmi(e))) return 'Closed'
    }
    const today = dayjs()
    if (emi?.dueDate && dayjs(emi.dueDate).isBefore(today)) return 'Overdue'
    return 'Unpaid'
  },
  principal_amount: ({ emi, payment }) =>
    parseVal(payment?.principalAmount || emi?.principalAmount).toFixed(2),
  interest_amount: ({ emi }) => parseVal(emi?.interestAmount).toFixed(2),
  penalty_amount: ({ emi, payment }) =>
    parseVal(payment?.latePaymentAmount || emi?.penaltyAmount).toFixed(2),
  customer_mobile: ({ loan, emi }) =>
    emi?.customer?.customerMobile?.[0] || loan?.customer?.customerMobile?.[0] || '-',
  amount_sanctioned: ({ loan }) =>
    parseFloat(loan?.loanDetails?.loanAmountSanctioned || loan?.loanAmountSanctioned || 0).toFixed(
      2
    ),
  vas_name: ({ vas }) => vas?.name || '-',
  vas_amount: ({ vas }) => (parseFloat(vas?.amount) || 0).toFixed(2),
  vas_due_date: ({ vas }) =>
    vas?.dueDate || vas?.startDate ? dayjs(vas.dueDate || vas.startDate).format('DD/MM/YYYY') : '-',
  outstanding_principal: ({ loan }) => {
    const emiSchedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    if (emiSchedule.length > 0) {
      const unpaidPrincipal = emiSchedule
        .filter((e) => !isPaidEmi(e))
        .reduce((sum, e) => sum + parseVal(e.principalAmount), 0)
      return unpaidPrincipal.toFixed(2)
    }
    return parseVal(loan?.loanDetails?.outstandingPrincipal || loan?.outstandingAmount).toFixed(2)
  },
  processing_fee: ({ loan }) => {
    const sanctioned = parseVal(loan?.loanDetails?.loanAmountSanctioned)
    const fees = loan?.loanDetails?.processingFee || []
    const totalShare = fees.reduce((sum, f) => sum + parseVal(f.share), 0)
    return ((sanctioned * totalShare) / 100).toFixed(2)
  },
  net_disbursed: ({ loan }) => {
    const sanctioned = parseVal(loan?.loanDetails?.loanAmountSanctioned)
    const fees = loan?.loanDetails?.processingFee || []
    const totalShare = fees.reduce((sum, f) => sum + parseVal(f.share), 0)
    const feeAmount = (sanctioned * totalShare) / 100
    // Simplified: sanctioned - fee (add GST logic if available in data)
    return (sanctioned - feeAmount).toFixed(2)
  },
  disbursal_date: ({ loan }) => {
    const date = loan?.loanDetails?.loanDisbursements?.[0]?.date
    return date ? dayjs(date).format('DD/MM/YYYY') : '-'
  },
  tenure: ({ loan }) => (loan?.tenure || loan?.loanDetails?.tenure || '-') + ' Months',
  ownership_type: ({ loan }) => 'Individual',

  // Collection & Visit Report Mappers
  visit_title: ({ task }) => task?.taskTitle || '-',
  visit_status: ({ task }) => task?.taskStatus || '-',
  visit_started: ({ task }) =>
    task?.actualStartDateTime ? dayjs(task.actualStartDateTime).format('DD/MM/YYYY hh:mm A') : '-',
  visit_completed: ({ task }) =>
    task?.actualEndDateTime ? dayjs(task.actualEndDateTime).format('DD/MM/YYYY hh:mm A') : '-',
  visit_assigned_to: ({ task }) =>
    task?.assignedTo ? `${task.assignedTo.firstName} ${task.assignedTo.lastName || ''}` : 'N/A',
  meeting_outcome: ({ task }) => task?.meetingDetails?.meetingOutCome || '-',

  // Additional Collection Mappers
  product_info: ({ loan }) => loan?.productInfo || '-',
  case_start_date: ({ loan }) =>
    loan?.caseStartDate ? dayjs(loan.caseStartDate).format('DD/MM/YYYY hh:mm A') : '-',
  case_end_date: ({ loan }) =>
    loan?.caseEndDate ? dayjs(loan.caseEndDate).format('DD/MM/YYYY hh:mm A') : '-',
  tat: ({ loan }) => {
    if (loan?.caseStartDate && loan?.caseEndDate) {
      const diff = dayjs(loan.caseEndDate).diff(dayjs(loan.caseStartDate))
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      return `${hours}h ${minutes}m`
    }
    return '-'
  },
  agent_id: ({ loan }) => {
    if (loan?.assignedTo && Array.isArray(loan.assignedTo) && loan.assignedTo.length > 0) {
      if (typeof loan.assignedTo[0] === 'object') {
        return loan.assignedTo.map((u) => u.employeeId || '-').join(', ')
      }
    }
    return loan?.createdBy?.employeeId || '-'
  },
  outcome: ({ loan }) => loan?.userDefinedCaseStatus || '-',
  crn: ({ loan }) => loan?.customer?.crn || '-',
  file_no: ({ loan }) => loan?.fileNo || loan?.caseNo || '-',
  cif_id: ({ loan }) => loan?.customer?.cifId || loan?.customer?.crn || '-',
  company_name: ({ loan }) => loan?.company || 'Zavron',
  centre_name: ({ loan, emi }, branches, teams) => getTeamName(loan, emi, teams),
  loan_category: ({ loan }) => loan?.loanCategory || 'General',
  purpose_of_loan: ({ loan }) => loan?.purposeOfLoan || '-',
  customer_no: ({ loan }) => loan?.customer?.customerMobile?.[0] || loan?.customer?.crn || '-',
  customer_email: ({ loan }) => loan?.customer?.customerEmail || '-',
  dues: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs())).length
  },
  due_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + parseVal(e.emiAmount), 0).toFixed(2)
  },
  pending_emi_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule
      .filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
      .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))
    return overdue[0] ? dayjs(overdue[0].dueDate).format('DD-MM-YYYY') : '-'
  },
  language: () => 'English',
  guarantor_name: ({ loan }) =>
    loan?.guarantorName ||
    loan?.loanDetails?.guarantors?.[0]?.customerFullName ||
    loan?.loanDetails?.guarantors?.[0]?.name ||
    '-',
  guarantor_no: ({ loan }) =>
    loan?.guarantorNo ||
    loan?.loanDetails?.guarantors?.[0]?.customerMobile?.[0] ||
    loan?.loanDetails?.guarantors?.[0]?.mobile ||
    '-',
  co_borrower_name: ({ loan }) =>
    loan?.coBorrowerName ||
    loan?.loanDetails?.coApplicants?.[0]?.customerFullName ||
    loan?.loanDetails?.coApplicants?.[0]?.name ||
    '-',
  co_borrower_cont_no: ({ loan }) =>
    loan?.coBorrowerContNo ||
    loan?.loanDetails?.coApplicants?.[0]?.customerMobile?.[0] ||
    loan?.loanDetails?.coApplicants?.[0]?.mobile ||
    '-',
  first_due_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const firstEmi = schedule.find((e) => parseFloat(e.emiAmount) > 0) || schedule[0]
    return firstEmi?.dueDate ? dayjs(firstEmi.dueDate).format('DD-MM-YYYY') : '-'
  },
  frequency: ({ loan }) =>
    loan?.loanDetails?.tenureType || loan?.loanDetails?.frequency || 'Monthly',
  total_amt: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const total = schedule.reduce((sum, e) => sum + (parseFloat(e.emiAmount) || 0), 0)
    return total > 0 ? total.toFixed(2) : '-'
  },
  first_installment: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const firstEmi = schedule.find((e) => parseFloat(e.emiAmount) > 0)
    const amt = firstEmi?.emiAmount || loan?.loanDetails?.emiAmount || 0
    return (parseFloat(amt) || 0).toFixed(2)
  },
  indicative_roi: ({ loan }) => {
    const rate = parseFloat(loan?.loanDetails?.interestRate || 0)
    const monthly = rate / 12
    return `${monthly.toFixed(2)}% (M) ${rate.toFixed(2)}% (Y)`
  },
  total_finance: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const total = schedule.reduce((sum, e) => sum + (parseFloat(e.emiAmount) || 0), 0)
    return total.toFixed(2)
  },
  region: ({ loan }) => loan?.region || 'Default Region',
  emi_type: ({ loan }) => loan?.loanDetails?.emiType || 'Diminishing',
  cumm_vas: ({ loan }) => (loan?.cummVas || 0).toFixed(2),
  vas_collected: ({ loan }) => (loan?.vasCollected || 0).toFixed(2),
  vas_due: ({ loan }) => (loan?.vasDue || 0).toFixed(2),
  cumm_handloan: ({ loan }) => (loan?.cummHandloan || 0).toFixed(2),
  handloan_collected: ({ loan }) => (loan?.handloanCollected || 0).toFixed(2),
  handloan_due: ({ loan }) => (loan?.handloanDue || 0).toFixed(2),
  comm_enabled: ({ loan }) => (loan?.communicationEnabled ? 'Yes' : 'No'),
  legal: ({ loan }) => (loan?.isLegalStatus ? 'Yes' : '-'),
  funding_type: ({ loan }) => loan?.fundingType || 'Self',
  last_emi_paid: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const paid = schedule
      .filter((e) => isPaidEmi(e))
      .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))
    return paid[0]?.paymentDate ? dayjs(paid[0].paymentDate).format('DD-MM-YYYY') : '-'
  },
  arrear_received: () => '0.00',
  prev_adv_coll: () => '0.00',
  month_tbc: () => '0.00',
  month_tbc_received: () => '0.00',
  month_collected: () => '0.00',
  gross_tbc: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + parseVal(e.emiAmount), 0).toFixed(2)
  },
  gross_demand: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.reduce((sum, e) => sum + parseVal(e.emiAmount), 0).toFixed(2)
  },
  gross_collected: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule
      .filter((e) => isPaidEmi(e))
      .reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
      .toFixed(2)
  },
  coll_demand_pct: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const total = schedule.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    const collected = schedule
      .filter((e) => isPaidEmi(e))
      .reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total > 0 ? ((collected / total) * 100).toFixed(2) : '0.00'
  },
  latest_status: ({ loan }) => {
    if (loan?.status === 'CLOSED') return 'Closed'
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const expiringDate = schedule.length > 0 ? dayjs(schedule[schedule.length - 1].dueDate) : null
    if (expiringDate && expiringDate.isBefore(dayjs())) return 'Expired'
    return loan?.status || 'Active'
  },
  effective_apr: ({ loan }) =>
    (loan?.loanDetails?.annualProcessingRate || loan?.loanDetails?.effectiveAPR || 0).toFixed(2),
  irr: ({ loan }) => (loan?.loanDetails?.irr || 0).toFixed(4),
  deductions: ({ loan }) => (loan?.loanDetails?.deductions || 0).toFixed(2),
  source_type: ({ loan }) =>
    loan?.sourceType || (loan?.loanDetails?.partner ? 'Partner' : 'Direct'),
  source_name: ({ loan }) => loan?.sourceName || loan?.loanDetails?.partner?.partnerName || '-',
  date_of_incorporation: ({ loan }) =>
    loan?.dateOfIncorporation || loan?.createdAt
      ? dayjs(loan.dateOfIncorporation || loan.createdAt).format('DD-MM-YYYY')
      : '-',
  collection_executive: ({ loan }) => {
    if (loan?.assignedTo && Array.isArray(loan.assignedTo) && loan.assignedTo.length > 0) {
      const exec = loan.assignedTo[0]
      if (typeof exec === 'object' && exec !== null) {
        return `${exec.firstName || ''} ${exec.lastName || ''}`.trim() || '-'
      }
      return exec // Return ID if not populated
    }
    return '-'
  },
  lead_owner: ({ loan }) => {
    if (loan?.leadOwner) return loan.leadOwner
    if (loan?.createdBy && typeof loan.createdBy === 'object') {
      return `${loan.createdBy.firstName || ''} ${loan.createdBy.lastName || ''}`.trim() || '-'
    }
    return '-'
  },
  renewed: ({ loan }) => (loan?.isRenewed ? 'Yes' : 'No'),
  product: ({ loan }) =>
    loan?.product || loan?.loanDetails?.productName || loan?.typeOfLoan || loan?.caseType || '-',
  customer_state: ({ loan }) => loan?.customer?.state || '-',
  area: ({ loan }) => loan?.customer?.area || '-',
  landmark: ({ loan }) => loan?.customer?.landmark || '-',
  loan_segment: ({ loan }) => loan?.loanSegment || 'General',
  scheme_name: ({ loan }) => loan?.loanDetails?.schemeName || '-',
  sanction_number: ({ loan }) => loan?.loanDetails?.sanctionNumber || loan?.caseNo || '-',
  simple_interest: ({ loan }) => {
    const rate = loan?.loanDetails?.interestRate || 0
    return `${rate.toFixed(2)}(M) ${(rate * 12).toFixed(2)}(Y)`
  },
  dealer_name: ({ loan }) => loan?.dealerName || '-',
  last_due_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.length > 0
      ? dayjs(schedule[schedule.length - 1].dueDate).format('DD-MM-YYYY')
      : '-'
  },
  last_installment_due_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.length > 0
      ? dayjs(schedule[schedule.length - 1].dueDate).format('DD-MM-YYYY')
      : '-'
  },
  disbursement_amount: ({ loan }) =>
    (
      loan?.loanDetails?.loanDisbursements?.[0]?.amount ||
      loan?.loanDetails?.totalAmountDisbursed ||
      0
    ).toFixed(2),
  disbursement_date: ({ loan }) => {
    const date = loan?.loanDetails?.loanDisbursements?.[0]?.date || loan?.emiStartDate
    return date ? dayjs(date).format('DD-MM-YYYY') : '-'
  },
  disbursement_status: ({ loan }) =>
    loan?.loanDetails?.disbursalStatus || loan?.loanDetails?.loanDisbursements?.[0]?.status || '-',
  credit_score: ({ loan }) => loan?.creditScore || loan?.loanDetails?.creditScore || '0',
  ckyc_id: ({ loan }) => loan?.ckycId || '-',
  finance_tags: ({ loan }) => {
    if (Array.isArray(loan?.tags) && loan.tags.length > 0) return loan.tags.join(', ')
    return loan?.financeTags || '-'
  },
  floating_rate: ({ loan }) => (loan?.isFloatingRate ? 'Yes' : 'No'),
  loan_insurance_amount: ({ loan }) => (loan?.insuranceAmount || 0).toFixed(2),
  loan_insurance_tenure: ({ loan }) => loan?.insuranceTenure || '0',

  // Detailed Due Report specific mappers
  customer_address: ({ loan }) => {
    if (loan?.customer?.customerAddress && Array.isArray(loan.customer.customerAddress)) {
      const addr = loan.customer.customerAddress[0]
      if (addr) {
        return `${addr.addressLine1 || ''} ${addr.addressLine2 || ''} ${addr.city || ''} ${
          addr.state || ''
        } ${addr.pinCode || ''}`.trim()
      }
    }
    return '-'
  },
  alternate_no: ({ loan }) =>
    loan?.customer?.references?.[0]?.mobile || loan?.customer?.alternateMobile || '-',
  district: ({ loan }) => loan?.customer?.district || '-',
  gender: ({ loan }) => loan?.customer?.gender || '-',
  loan_value: ({ loan }) => (loan?.loanDetails?.loanAmountRequested || 0).toFixed(2),
  guarantor_address: ({ loan }) => {
    const g = loan?.loanDetails?.guarantors?.[0]
    if (g?.address) return g.address
    return '-'
  },
  bank_name: ({ loan }) =>
    loan?.loanDetails?.loanDisbursements?.[0]?.bankDetails?.bankName ||
    loan?.loanDetails?.partner?.partnerName ||
    '-',
  cheque_no: ({ loan }) =>
    loan?.loanDetails?.loanDisbursements?.[0]?.referenceNo ||
    loan?.loanDetails?.loanDisbursements?.[0]?.chequeDetails?.chequeNumber ||
    '-',
  down_payment: ({ loan }) => (loan?.loanDetails?.downPayment || 0).toFixed(2),
  monthly_tbc: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const totalDue = schedule.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return totalDue.toFixed(2)
  },
  installment_dues: ({ emi }) => parseVal(emi?.emiAmount).toFixed(2),
  advance_emi: ({ loan }) => parseVal(loan?.loanDetails?.advanceEmi).toFixed(2),
  lcc_percent: ({ loan }) => parseVal(loan?.loanDetails?.latePaymentFee).toFixed(2),
  loan_cycle_no: ({ loan }) => loan?.loanCycleNo || '1',
  ltv: ({ loan }) => parseVal(loan?.ltv).toFixed(2),
  arrears_count: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const arrears = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    return arrears.length
  },
  arrears_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const arrears = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    const total = arrears.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total.toFixed(2)
  },
  overdue_principal: ({ emi }) => {
    if (!isPaidEmi(emi) && dayjs(emi?.dueDate).isBefore(dayjs())) {
      return (emi.principalAmount || 0).toFixed(2)
    }
    return '0.00'
  },
  overdue_interest: ({ emi }) => {
    if (!isPaidEmi(emi) && dayjs(emi?.dueDate).isBefore(dayjs())) {
      return (emi.interestAmount || 0).toFixed(2)
    }
    return '0.00'
  },
  excess_principal: () => '0.00',
  excess_interest: () => '0.00',
  insurance_company: ({ loan }) => loan?.insuranceCompany || '-',
  policy_branch: ({ loan }) => loan?.policyBranch || '-',
  id_proof: ({ loan }) => loan?.customer?.idProof || '-',
  insurance_expiry: ({ loan }) =>
    loan?.insuranceExpiry ? dayjs(loan.insuranceExpiry).format('DD-MM-YYYY') : '-',
  insurance_policy_no: ({ loan }) => loan?.insurancePolicyNo || '-',
  balance_outstanding: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const unpaid = schedule.filter((e) => !isPaidEmi(e))
    const total = unpaid.reduce((sum, e) => sum + parseVal(e.principalAmount), 0)
    return total.toFixed(2)
  },
  employment_type: ({ loan }) => loan?.customer?.employmentType || '-',
  cumulative_demand: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const dueTillDate = schedule.filter((e) => dayjs(e.dueDate).isBefore(dayjs()))
    const total = dueTillDate.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total.toFixed(2)
  },
  cumulative_collection: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const collected = schedule.filter((e) => isPaidEmi(e))
    const total = collected.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total.toFixed(2)
  },
  future_flow: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const future = schedule.filter((e) => dayjs(e.dueDate).isAfter(dayjs()))
    const total = future.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total.toFixed(2)
  },
  npa_status: ({ loan }) => loan?.npaStatus || 'N',
  security_value: ({ loan }) => parseVal(loan?.securityValue).toFixed(2),
  vas_dues: ({ loan }) => parseVal(loan?.vasDues).toFixed(2),

  // PAR Report specific mappers
  amount_not_due: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const future = schedule.filter((e) => dayjs(e.dueDate).isAfter(dayjs()))
    const total = future.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    return total.toFixed(2)
  },
  overdue_principal_total: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + (parseFloat(e.principalAmount) || 0), 0).toFixed(2)
  },
  overdue_interest_total: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + (parseFloat(e.interestAmount) || 0), 0).toFixed(2)
  },
  processing_fees: ({ loan }) => {
    const charges = loan?.additionalCharges || loan?.loanDetails?.additionalCharges || []
    const pf = charges.find((c) => c.name.toLowerCase().includes('processing'))
    return (parseFloat(pf?.amount) || 0).toFixed(2)
  },
  last_installment_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    if (schedule.length > 0) {
      return dayjs(schedule[schedule.length - 1].dueDate).format('DD-MM-YYYY')
    }
    return '-'
  },
  par_1_29: ({ loan }) => getParBucketValue(loan, 1, 29),
  par_30_59: ({ loan }) => getParBucketValue(loan, 30, 59),
  par_60_89: ({ loan }) => getParBucketValue(loan, 60, 89),
  par_90_119: ({ loan }) => getParBucketValue(loan, 90, 119),
  par_120_149: ({ loan }) => getParBucketValue(loan, 120, 149),
  par_150_179: ({ loan }) => getParBucketValue(loan, 150, 179),
  par_180_365: ({ loan }) => getParBucketValue(loan, 180, 365),
  par_above_365: ({ loan }) => getParBucketValue(loan, 366, 99999),
  total_par: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const hasOverdue = schedule.some((e) => getOverdueDays(e) > 0)
    if (!hasOverdue) return '0.00'
    const disAmount = Number(
      loan?.loanDetails?.loanDisbursements?.[0]?.amount ||
      loan?.loanDetails?.totalAmountDisbursed ||
      0
    )
    return disAmount.toFixed(2)
  },

  // EMI Due Report specific mappers
  father_spouse: ({ loan }) => loan?.customer?.fatherSpouseName || 'NA',
  dues_count: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const arrears = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    const totalDue = arrears.reduce((sum, e) => sum + parseVal(e.emiAmount), 0)
    const instAmt = parseVal(schedule[0]?.emiAmount) || 1
    return (totalDue / instAmt).toFixed(2)
  },
  lpc_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    // Sum of all penalty/late fees
    const total = schedule.reduce((sum, e) => sum + parseVal(e.penaltyAmount), 0)
    return total > 0 ? total.toFixed(2) : '0'
  },
  handloan: () => '0',
  last_payment_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const paid = schedule
      .filter((e) => isPaidEmi(e))
      .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))
    return paid[0]?.emiAmount ? parseVal(paid[0].emiAmount).toFixed(2) : '0'
  },
  city: ({ loan }) => loan?.customer?.city || '-',
  consultant: ({ loan }) => loan?.consultant || '-',
  installments_received: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => isPaidEmi(e)).length
  },
  balance_emis: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => !isPaidEmi(e)).length
  },
  nach_regd: ({ loan }) =>
    loan?.isNachRegistered || loan?.loanDetails?.nachRegistered ? 'Yes' : 'No',
  pdc_available: ({ loan }) =>
    loan?.isPdcAvailable || loan?.loanDetails?.pdcAvailable ? 'Yes' : 'No',

  // Collection Report specific mappers
  channel: ({ emi }) => emi?.paymentChannel || emi?.paymentMethod || 'Deposit',
  instrument_no: ({ emi }) => emi?.referenceNo || emi?.transactionId || '-',
  ledger_name: () => 'CF_zavron_RP',
  collection_charges: () => '0',
  group_name: () => '-',
  partial_emi_no: () => '-',
  partial_emi_date: () => '-',
  partial_amount: () => '0',
  full_amount: () => '0',
  fin_status: ({ loan }) => loan?.status || '-',
  settlement_amount: () => '0',
  branch_prefix: ({ loan }) => {
    const no = loan?.caseNo || loan?.applicationNumber || ''
    return no.substring(0, 5) || 'SaleF'
  },
  roi_yearly: ({ loan }) => {
    const rate = loan?.loanDetails?.interestRate || 0
    return `${(rate * 12).toFixed(2)}(Y)`
  },
  accepted_at: ({ emi }) => {
    // Usually one day after payment or same day
    return emi?.paymentDate ? dayjs(emi.paymentDate).add(1, 'day').format('DD-MM-YYYY HH:mm') : '-'
  },
  foreclosure_charges: () => '0.00',
  bounce_charges: ({ emi }) => (emi?.penaltyAmount || 0).toFixed(2),

  // DPD Report Specific Mappers
  centre: ({ loan, emi }, branches, teams) => getTeamName(loan, emi, teams),
  customer_mobile_dpd: ({ loan }) => loan?.customer?.customerMobile?.[0] || '-',
  source: ({ loan }) => loan?.loanDetails?.sourceType || loan?.sourceType || 'Customer',
  source_name_dpd: ({ loan }) => loan?.loanDetails?.sourceName || loan?.sourceName || '-',
  sub_source: ({ loan }) => loan?.loanDetails?.subSourceName || loan?.subSourceName || '-',
  interest_type: ({ loan }) => loan?.loanDetails?.typeOfInterest || 'Diminishing',
  first_installment_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const firstEmi = schedule.find((e) => parseFloat(e.emiAmount) > 0)
    const amt = firstEmi?.emiAmount || loan?.loanDetails?.emiAmount || 0
    return (parseFloat(amt) || 0).toFixed(2)
  },
  installment_start_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const firstEmi = schedule.find((e) => parseFloat(e.emiAmount) > 0) || schedule[0]
    return firstEmi?.dueDate ? dayjs(firstEmi.dueDate).format('DD-MM-YYYY') : '-'
  },
  installments_received_on_time: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => isPaidEmi(e) && !dayjs(e.paymentDate).isAfter(dayjs(e.dueDate)))
      .length
  },
  current_dpd: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    if (!overdue.length) return 0
    const oldest = overdue.sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
    return dayjs().diff(dayjs(oldest.dueDate), 'day')
  },
  cumulative_overdue: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + parseVal(e.emiAmount), 0).toFixed(2)
  },
  // Balance Sheet Mappers (Horizontal)
  le_particulars: ({ le_particulars }) => le_particulars || '',
  le_amount: ({ le_amount }) => le_amount || '',
  assets_particulars: ({ assets_particulars }) => assets_particulars || '',
  assets_amount: ({ assets_amount }) => assets_amount || '',

  bs_category: ({ category }) => category || '',
  bs_particulars: ({ particulars }) => particulars || '',
  bs_assets: ({ income, amount }) => {
    const val = amount !== undefined ? amount : income
    const valParsed = parseVal(val)
    return val !== null && val !== 0 ? valParsed.toFixed(2) : ''
  },
  bs_liabilities: ({ expense }) => {
    const expParsed = parseVal(expense)
    return expense !== null && expense !== 0 ? expParsed.toFixed(2) : ''
  },
  cumulative_overdue_collected: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const collectedOverdue = schedule.filter(
      (e) => isPaidEmi(e) && dayjs(e.paymentDate).isAfter(dayjs(e.dueDate))
    )
    return collectedOverdue.reduce((sum, e) => sum + parseVal(e.emiAmount), 0).toFixed(2)
  },

  // Trial Balance Mappers
  tb_group_name: ({ group }) => group?.name || '',
  tb_ledger_name: ({ ledger }) => ledger?.name || '',
  tb_debit: ({ summary }) =>
    summary?.totalDebit === null ? '' : parseVal(summary?.totalDebit).toFixed(2),
  tb_credit: ({ summary }) =>
    summary?.totalCredit === null ? '' : parseVal(summary?.totalCredit).toFixed(2),
  tb_closing_balance: ({ summary }) => {
    if (summary?.totalDebit === null) return ''
    const balance = parseVal(summary?.totalDebit) - parseVal(summary?.totalCredit)
    return balance.toFixed(2)
  },

  // P&L Mappers
  pnl_category: ({ category }) => category || '',
  pnl_particulars: ({ particulars, particulate }) => particulars || particulate || '',
  pnl_income: ({ type, amount, income }) => {
    if (income !== undefined && income !== null) return income !== 0 ? parseVal(income).toFixed(2) : ''
    return type === 'INCOME' ? parseVal(amount).toFixed(2) : ''
  },
  pnl_expense: ({ type, amount, expense }) => {
    if (expense !== undefined && expense !== null) return expense !== 0 ? parseVal(expense).toFixed(2) : ''
    return type === 'EXPENSE' ? parseVal(amount).toFixed(2) : ''
  },

  // NEW Mappers for Requested Reports
  AgreementNo: ({ loan }) => loan?.caseNo || loan?.applicationNumber || '-',
  Branch: ({ loan, emi }, branches, teams) => getBranchName(loan, emi, branches),
  CentreName: ({ loan, emi }, branches, teams) => getTeamName(loan, emi, teams),
  CustomerName: ({ loan }) => loan?.customer?.customerFullName || '-',
  MobileNumber: ({ loan }) => loan?.customer?.customerMobile?.[0] || '-',
  TransactionDate: ({ emi, payment, loan }) => {
    const date =
      payment?.date ||
      emi?.paymentDate ||
      loan?.loanDisbursements?.[0]?.date ||
      loan?.loanDetails?.loanDisbursements?.[0]?.date
    return date ? dayjs(date).format('DD-MM-YYYY') : '-'
  },
  ModeOfPayment: ({ payment, loan }) =>
    payment?.paymentMode ||
    loan?.loanDisbursements?.[0]?.paymentMode ||
    loan?.loanDetails?.loanDisbursements?.[0]?.paymentMode ||
    'Cash',
  CollectionExecutive: ({ loan }) => {
    const exec = loan?.assignedTo?.[0]
    if (!exec) return '-'
    if (typeof exec === 'object') return `${exec.firstName || ''} ${exec.lastName || ''}`.trim()
    return exec
  },
  CreatedBy: ({ loan }) => {
    const creator = loan?.createdBy
    if (!creator) return '-'
    if (typeof creator === 'object')
      return `${creator.firstName || ''} ${creator.lastName || ''}`.trim()
    return creator
  },
  Installment: ({ emi }) => parseVal(emi?.emiAmount).toFixed(2),
  LPC: ({ emi, payment }) => parseVal(payment?.latePaymentAmount || emi?.penaltyAmount).toFixed(2),
  PrincipalAmount: ({ emi, payment, loan }) => {
    const amount =
      payment?.principalAmount ||
      emi?.principalAmount ||
      loan?.loanDisbursements?.[0]?.amount ||
      loan?.loanDetails?.loanDisbursements?.[0]?.amount ||
      0
    return parseVal(amount).toFixed(2)
  },
  Interest: ({ emi }) => parseVal(emi?.interestAmount).toFixed(2),
  InstallmentNo: ({ emi }) => emi?.emiNo || '-',
  InstallmentDate: ({ emi }) => (emi?.dueDate ? dayjs(emi.dueDate).format('DD-MM-YYYY') : '-'),
  VoucherNo: ({ payment, emi, loan }) =>
    payment?.voucherNo ||
    emi?.voucherNo ||
    loan?.loanDisbursements?.[0]?.voucherNo ||
    loan?.loanDisbursements?.[0]?.referenceNo ||
    loan?.loanDetails?.loanDisbursements?.[0]?.voucherNo ||
    loan?.loanDetails?.loanDisbursements?.[0]?.referenceNo ||
    '-',
  InstrumentNo: ({ payment, emi }) => payment?.instrumentNo || emi?.referenceNo || '-',
  DueAmount: ({ emi }) => parseVal(emi?.emiAmount).toFixed(2),
  Status: ({ emi }) => emi?.emiStatus || '-',
  AmountPaid: ({ payment, emi, rowData }) => {
    if (rowData?.total_paid !== undefined) return parseVal(rowData.total_paid).toFixed(2)
    return parseVal(payment?.amount || emi?.emiAmount).toFixed(2)
  },
  CollectionCharges: ({ payment }) => parseVal(payment?.collectionCharges).toFixed(2),
  GroupName: () => '-',
  PartiallyInstallmentNo: () => '-',
  PartiallyInstallmentNoDate: () => '-',
  PartiallyAmount: () => '0',
  FullAmount: () => '0',
  MonthlyInstallmentAmount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return parseVal(schedule[0]?.emiAmount).toFixed(2)
  },
  FinStatus: ({ loan }) => loan?.status || '-',
  SettlementReceiptAmount: () => '0',
  SubLoanType: ({ loan }) => loan?.typeOfLoan || '-',
  LoanSegment: ({ loan }) => loan?.loanSegment || 'General',
  BranchPrefix: ({ loan }) =>
    (loan?.caseNo || loan?.applicationNumber || '').substring(0, 5) || 'SaleF',
  ROI: ({ loan }) => {
    const rate = parseVal(loan?.loanDetails?.interestRate)
    return `${rate.toFixed(2)}(Y)`
  },
  SourceType: ({ loan }) => loan?.sourceType || 'Direct',
  SourceName: ({ loan }) => loan?.sourceName || '-',
  CustomerState: ({ loan }) => loan?.customer?.state || '-',
  AcceptedDateTime: ({ emi, payment }) => {
    const date = payment?.date || emi?.paymentDate
    return date ? dayjs(date).format('DD-MM-YYYY HH:mm') : '-'
  },
  ForeClosureCharges: () => '0.00',
  BounceCharges: ({ emi }) => (emi?.penaltyAmount || 0).toFixed(2)
}

// Helper for PAR buckets
function getParBucketValue(loan, min, max) {
  const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
  let oldestDpd = 0
  schedule.forEach((e) => {
    const dpd = getOverdueDays(e)
    if (dpd > oldestDpd) {
      oldestDpd = dpd
    }
  })

  if (oldestDpd >= min && oldestDpd <= max) {
    const disAmount = Number(
      loan?.loanDetails?.loanDisbursements?.[0]?.amount ||
      loan?.loanDetails?.totalAmountDisbursed ||
      0
    )
    return disAmount.toFixed(2)
  }
  return '0.00'
}

const CIBIL_HEADERS = [
  '[PN] Name Segment Tag',
  '[PN] Consumer Name Tag',
  '[PN] Consumer Name Field',
  '[PN] DOB Tag',
  '[PN] Date of Birth',
  '[PN] Gender Tag',
  '[PN] Gender',

  '[ID] ID Segment Tag',
  '[ID] ID Type',
  '[ID] ID Number Tag',
  '[ID] ID Number',

  '[PT] Phone Segment Tag',
  '[PT] Cell Tag',
  '[PT] Telephone Number',
  '[PT] Telephone Type',

  '[EC] Email Segment Tag',
  '[EC] E-Mail Tag',
  '[EC] Email ID',

  '[PA] Address Segment Tag',
  '[PA] Address Line1 Tag',
  '[PA] Address Line1',
  '[PA] Address Line2 Tag',
  '[PA] Address Line2',
  '[PA] Address Line3 Tag',
  '[PA] Address Line3',
  '[PA] Address Line4 Tag',
  '[PA] Address Line4',
  '[PA] Address Pin',
  '[PA] State Code Tag',
  '[PA] State Code',
  '[PA] PIN Code Tag',
  '[PA] PIN Code',
  '[PA] Address Category',

  '[TL] Account Segment Tag',
  '[TL] Member Code Tag',
  '[TL] Current/New Reporting Member Code',
  '[TL] Member Name Tag',
  '[TL] Current/New Member Short Name',
  '[TL] card seg Tag',
  '[TL] Account No',
  '[TL] Account Type',
  '[TL] Member type Tag',
  '[TL] Member Type',
  '[TL] Date Opened/Disbursed Tag',
  '[TL] Date Opened/Disbursed',
  '[TL] Last Payment Date Tag',
  '[TL] Last Payment Date',
  '[TL] Date Closed Tag',
  '[TL] Date Closed',
  '[TL] Date Reported and Certified Tag',
  '[TL] Date Reported and Certified',
  '[TL] Sanctioned Amount Tag',
  '[TL] High Credit/Sanctioned Amount',
  '[TL] Current Balance Tag',
  '[TL] Current Balance',
  '[TL] Amount Overdue Tag',
  '[TL] Amount Overdue',
  '[TL] Past Due Tag',
  '[TL] Number of Days Past Due',
  '[TL] Old Member Code Tag',
  '[TL] Old Member Code',
  '[TL] Old Member Short Name Tag',
  '[TL] Old Member Short Name',
  '[TL] Old Account Tag',
  '[TL] Old Account Number',
  '[TL] Credit Facility Tag',
  '[TL] Credit Facility',
  '[TL] Asset Tag',
  '[TL] Asset',
  '[TL] Security Amount Tag',
  '[TL] Security Amount',
  '[TL] Security Type Tag',
  '[TL] Security Type',
  '[TL] Cr Dr Limit Tag',
  '[TL] Cr Dr Limit',
  '[TL] Cash Limit Tag',
  '[TL] Cash Limit',
  '[TL] Written-off Amount Tag',
  '[TL] Written-off Amount',
  '[TL] Writeoff Principal Amount Tag',
  '[TL] Writeoff Principal Amount',
  '[TL] Settlement Amount Tag',
  '[TL] Settlement Amount',
  '[TL] Pay Frequency Tag',
  '[TL] Pay Frequency',
  '[TL] Occupation Tag',
  '[TL] Occupation',

  '[ES] End Segment'
]

const calculateAssetInfo = (loan, rules = []) => {
  if (!rules || !rules.length) return { category: 'Standard', provisioningAmount: 0, dpd: 0 }

  const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
  const oldestUnpaid = schedule
    .filter((e) => !isPaidEmi(e))
    .sort((a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix())[0]

  let dpd = 0
  if (oldestUnpaid) {
    dpd = Math.max(0, dayjs().diff(dayjs(oldestUnpaid.dueDate), 'days'))
  }

  const category = loan.caseType || loan.loanDetails?.loanCategory || 'Personal loan'
  const matchingRule = rules.find(
    (r) => r.category.toLowerCase() === category.toLowerCase() && dpd >= r.minDpd && dpd <= r.maxDpd
  )

  const principalOutstanding = parseFloat(loan.loanDetails?.principalOutstanding || 0)
  const provisioningAmount = matchingRule
    ? (principalOutstanding * matchingRule.provisioningPercentage) / 100
    : 0

  return {
    category: matchingRule ? matchingRule.key : 'Standard',
    provisioningAmount: provisioningAmount,
    dpd
  }
}

export const REPORT_COLUMNS = {
  'Loan Collection Report': {
    getRows: (loans, filters) => {
      const rows = []
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        const collectedEmis = emiSchedule.filter((emi) => {
          if (!isPaidEmi(emi)) return false
          if (filters && filters.start && filters.end) {
            const dateToCheck = emi.paymentDate || emi.dueDate
            if (!dateToCheck) return true
            const pDate = dayjs(dateToCheck)
            const start = dayjs(filters.start).startOf('day')
            const end = dayjs(filters.end).endOf('day')
            return (
              (pDate.isAfter(start) || pDate.isSame(start, 'day')) &&
              (pDate.isBefore(end) || pDate.isSame(end, 'day'))
            )
          }
          return true
        })

        if (collectedEmis.length > 0) {
          // Add a property to loan to hold aggregated collection data for mappers if needed
          const totalPaid = collectedEmis.reduce((sum, emi) => {
            const payments = emi.emiPayments || []
            if (payments.length > 0) {
              return sum + payments.reduce((pSum, p) => pSum + (parseFloat(p.amount) || 0), 0)
            }
            return sum + (parseFloat(emi.emiAmount) || 0)
          }, 0)

          rows.push({ loan, rowData: { total_paid: totalPaid } })
        }
      })
      return rows
    },
    columns: [
      { text: 'AgreementNo', value: 'AgreementNo' },
      { text: 'Branch', value: 'Branch' },
      { text: 'CentreName', value: 'CentreName' },
      { text: 'CustomerName', value: 'CustomerName' },
      { text: 'MobileNumber', value: 'MobileNumber' },
      { text: 'TransactionDate', value: 'TransactionDate' },
      { text: 'ModeOfPayment', value: 'ModeOfPayment' },
      { text: 'Channel', value: 'Channel' },
      { text: 'CollectionExecutive', value: 'CollectionExecutive' },
      { text: 'CreatedBy', value: 'CreatedBy' },
      { text: 'Installment', value: 'Installment' },
      { text: 'LPC', value: 'LPC' },
      { text: 'PrincipalAmount', value: 'PrincipalAmount' },
      { text: 'Interest', value: 'Interest' },
      { text: 'InstallmentNo', value: 'InstallmentNo' },
      { text: 'InstallmentDate', value: 'InstallmentDate' },
      { text: 'VoucherNo', value: 'VoucherNo' },
      { text: 'InstrumentNo', value: 'InstrumentNo' },
      { text: 'LedgerName', value: 'LedgerName' },
      { text: 'DueAmount', value: 'DueAmount' },
      { text: 'Status', value: 'Status' },
      { text: 'AmountPaid', value: 'AmountPaid' },
      { text: 'CollectionCharges', value: 'CollectionCharges' },
      { text: 'GroupName', value: 'GroupName' },
      { text: 'PartiallyInstallmentNo', value: 'PartiallyInstallmentNo' },
      { text: 'PartiallyInstallmentNoDate', value: 'PartiallyInstallmentNoDate' },
      { text: 'PartiallyAmount', value: 'PartiallyAmount' },
      { text: 'FullAmount', value: 'FullAmount' },
      { text: 'MonthlyInstallmentAmount', value: 'MonthlyInstallmentAmount' },
      { text: 'FinStatus', value: 'FinStatus' },
      { text: 'SettlementReceiptAmount', value: 'SettlementReceiptAmount' },
      { text: 'SubLoanType', value: 'SubLoanType' },
      { text: 'LoanSegment', value: 'LoanSegment' },
      { text: 'BranchPrefix', value: 'BranchPrefix' },
      { text: 'ROI', value: 'ROI' },
      { text: 'SourceType', value: 'SourceType' },
      { text: 'SourceName', value: 'SourceName' },
      { text: 'CustomerState', value: 'CustomerState' },
      { text: 'AcceptedDateTime', value: 'AcceptedDateTime' },
      { text: 'ForeClosureCharges', value: 'ForeClosureCharges' },
      { text: 'BounceCharges', value: 'BounceCharges' }
    ]
  },
  'Daily Collection Status Report': {
    getRows: (loans, filters) => {
      const agentSummary = {}
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        emiSchedule.forEach((emi) => {
          if (!isPaidEmi(emi)) return

          const payments = emi.emiPayments || []
          if (payments.length > 0) {
            payments.forEach((payment) => {
              if (payment.isDeleted) return
              if (filters && filters.start && filters.end) {
                const pDate = dayjs(payment.date)
                const start = dayjs(filters.start).startOf('day')
                const end = dayjs(filters.end).endOf('day')
                if (pDate.isBefore(start) || pDate.isAfter(end)) return
              }

              const agentId = loan.assignedTo?.[0] || 'Unassigned'
              const date = payment.date ? dayjs(payment.date).format('DD/MM/YYYY') : 'N/A'
              const key = `${agentId}_${date}`
              if (!agentSummary[key]) {
                agentSummary[key] = {
                  rowData: {
                    agent_name: loan.createdBy
                      ? `${loan.createdBy.firstName} ${loan.createdBy.lastName || ''}`
                      : 'N/A',
                    date: date,
                    collected_amount: 0,
                    target_amount: 100000,
                    branch: 'Head Office'
                  }
                }
              }
              agentSummary[key].rowData.collected_amount += parseFloat(payment.amount || 0)
            })
          } else {
            // Fallback for legacy data
            const dateToCheck = emi.paymentDate || emi.dueDate
            if (filters && filters.start && filters.end && dateToCheck) {
              const pDate = dayjs(dateToCheck)
              const start = dayjs(filters.start).startOf('day')
              const end = dayjs(filters.end).endOf('day')
              if (pDate.isBefore(start) || pDate.isAfter(end)) return
            }

            const agentId = loan.assignedTo?.[0] || 'Unassigned'
            const date = dateToCheck ? dayjs(dateToCheck).format('DD/MM/YYYY') : 'N/A'
            const key = `${agentId}_${date}`
            if (!agentSummary[key]) {
              agentSummary[key] = {
                rowData: {
                  agent_name: loan.createdBy
                    ? `${loan.createdBy.firstName} ${loan.createdBy.lastName || ''}`
                    : 'N/A',
                  date: date,
                  collected_amount: 0,
                  target_amount: 100000,
                  branch: 'Head Office'
                }
              }
            }
            agentSummary[key].rowData.collected_amount += parseFloat(emi.emiAmount || 0)
          }
        })
      })
      return Object.values(agentSummary)
    },
    columns: [
      { text: 'Date', value: 'date' },
      { text: 'Agent Name', value: 'agent_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collected Amount', value: 'collected_amount' },
      { text: 'Target Amount', value: 'target_amount' },
      { text: 'Achievement %', value: 'achievement_percent' }
    ]
  },
  'EMI DUE Report': {
    getRows: (loans, filters, rules = []) => {
      const rows = []
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        const unpaidEmis = emiSchedule.filter((e) => {
          if (isPaidEmi(e)) return false
          if (filters && filters.start && filters.end) {
            const dDate = dayjs(e.dueDate)
            return (
              (dDate.isAfter(dayjs(filters.start)) || dDate.isSame(dayjs(filters.start), 'day')) &&
              (dDate.isBefore(dayjs(filters.end)) || dDate.isSame(dayjs(filters.end), 'day'))
            )
          }
          return true
        })

        if (unpaidEmis.length > 0) {
          loan.assetInfo = calculateAssetInfo(loan, rules)
          unpaidEmis.forEach((emi) => rows.push({ loan, emi }))
        }
      })
      return rows
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Customer Contact (CRN)', value: 'customer_contact' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'City', value: 'city' },
      { text: 'State', value: 'customer_state' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'EMI No', value: 'emi_no' },
      { text: 'EMI Amount Due', value: 'emi_amount_due' },
      { text: 'Principal Amount', value: 'principal_amount' },
      { text: 'Interest Amount', value: 'interest_amount' },
      { text: 'Due Date', value: 'due_date' },
      { text: 'EMI Status', value: 'emi_status' },
      { text: 'Pending Days (DPD)', value: 'pending_days' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'Loan Amount Sanctioned', value: 'amount_sanctioned' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' },
      { text: 'Disbursal Date', value: 'disbursement_date' },
      { text: 'Tenure', value: 'tenure' },
      { text: 'Interest Rate', value: 'interest_rate' },
      { text: 'Product', value: 'product' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre Name', value: 'centre_name' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Last Payment Date', value: 'last_payment_date' },
      { text: 'Last Payment Amount', value: 'last_payment_amount' },
      { text: 'Installments Received', value: 'installments_received' },
      { text: 'Balance EMIs', value: 'balance_emis' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Asset Category', value: 'asset_category' },
      { text: 'Provisioning Amount', value: 'provisioning_amount' }
    ]
  },
  'Detailed Due Report': {
    getRows: (loans, filters, rules = []) => {
      // Return one row per loan that has overdue EMIs
      return loans
        .filter((loan) => {
          const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return schedule.some((e) => {
            const isUnpaid = !isPaidEmi(e)
            const isOverdue = dayjs(e.dueDate).isBefore(dayjs())
            if (!isUnpaid || !isOverdue) return false

            if (filters && filters.start && filters.end) {
              const dDate = dayjs(e.dueDate)
              return (
                (dDate.isAfter(dayjs(filters.start)) ||
                  dDate.isSame(dayjs(filters.start), 'day')) &&
                (dDate.isBefore(dayjs(filters.end)) || dDate.isSame(dayjs(filters.end), 'day'))
              )
            }
            return true
          })
        })
        .map((loan) => {
          loan.assetInfo = calculateAssetInfo(loan, rules)
          return { loan, emi: {} }
        })
    },
    columns: [
      { text: 'Branch Name', value: 'branch' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'File No', value: 'file_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'Customer Contact No', value: 'customer_no' },
      { text: 'Alternate Contact No', value: 'alternate_no' },
      { text: 'Area', value: 'area' },
      { text: 'District', value: 'district' },
      { text: 'State', value: 'customer_state' },
      { text: 'Gender', value: 'gender' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'Loan Value', value: 'loan_value' },
      { text: 'Loan Amount', value: 'amount_sanctioned' },
      { text: 'Guarantor Name', value: 'guarantor_name' },
      { text: 'Guarantor Address', value: 'guarantor_address' },
      { text: 'Gurantor Mobile No', value: 'guarantor_no' },
      { text: 'Centre Name', value: 'centre_name' },
      { text: 'Collection Frequency', value: 'frequency' },
      { text: 'Date of Disbursment', value: 'disbursement_date' },
      { text: 'Date of First Installment', value: 'installment_start_date' },
      { text: 'Date of Last Installment', value: 'last_installment_date' },
      { text: 'Disbursment Bank Name', value: 'bank_name' },
      { text: 'Disbursment Chq.No', value: 'cheque_no' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' },
      { text: 'Down Payment', value: 'down_payment' },
      { text: 'Monthly TBC', value: 'monthly_tbc' },
      { text: 'Installment Dues', value: 'dues_count' },
      { text: 'No of Installments', value: 'tenure_count' },
      { text: 'Installment Amount', value: 'first_installment' },
      { text: 'Interest Rate', value: 'interest_rate' },
      { text: 'IRR', value: 'irr' },
      { text: 'Advance EMI', value: 'advance_emi' },
      { text: 'Last Receipt Date', value: 'last_payment_date' },
      { text: 'LCC(%)', value: 'lcc_percent' },
      { text: 'Loan Cycle No', value: 'loan_cycle_no' },
      { text: 'LTV', value: 'ltv' },
      { text: 'Mode of Repayment', value: 'payment_mode' },
      { text: 'Arrears in Number', value: 'dues_count' },
      { text: 'Arrears in Amount', value: 'arrears_amount' },
      { text: 'DPD', value: 'current_dpd' },
      { text: 'Principal Outstanding', value: 'outstanding_principal' },
      { text: 'Over Due Principal Amount', value: 'overdue_principal_total' },
      { text: 'Over Due Interest Amount', value: 'overdue_interest_total' },
      { text: 'Excess principal', value: 'excess_principal' },
      { text: 'Excess Interest', value: 'excess_interest' },
      { text: 'Name of the Insurance Co.', value: 'insurance_company' },
      { text: 'Policy Issued Branch', value: 'policy_branch' },
      { text: 'ID Proof', value: 'id_proof' },
      { text: 'Ins. Expiry Date', value: 'insurance_expiry' },
      { text: 'Ins. Policy No.', value: 'insurance_policy_no' },
      { text: 'Balance Outstanding', value: 'balance_outstanding' },
      { text: 'EmploymentType', value: 'employment_type' },
      { text: 'Cumulative Demand', value: 'cumulative_demand' },
      { text: 'Cumulative Collection', value: 'cumulative_collection' },
      { text: 'Future Flow Amt in Rs.', value: 'future_flow' },
      { text: 'Processing Fees', value: 'processing_fees' },
      { text: 'NPA', value: 'npa_status' },
      { text: 'Source Type', value: 'source_type' },
      { text: 'Source Name', value: 'source_name' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Security Value', value: 'security_value' },
      { text: 'VAS Dues', value: 'vas_dues' },
      { text: 'Finance Tags', value: 'finance_tags' }
    ]
  },
  'Non Starter Report': {
    getRows: (loans, filters) => {
      const rows = []
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        if (!emiSchedule.length) return

        // Find the first EMI installment
        const firstEmi = emiSchedule.find((e) => e.emiNo === 1) || emiSchedule[0]

        // If the first EMI is unpaid, this is a non-starter loan
        if (firstEmi && !isPaidEmi(firstEmi)) {
          rows.push({ loan, emi: firstEmi })
        }
      })
      return rows
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'EMI Start Date', value: 'due_date' },
      { text: 'EMI Amount', value: 'emi_amount_due' },
      { text: 'EMI Status', value: 'emi_status' },
      { text: 'Loan Amount Sanctioned', value: 'amount_sanctioned' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Tenure', value: 'tenure' },
      { text: 'Interest Rate', value: 'interest_rate' },
      { text: 'Disbursal Date', value: 'disbursement_date' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' }
    ]
  },
  'Loan Expiry Report': {
    getRows: (loans, filters) => {
      const today = dayjs()
      return loans
        .filter((loan) => {
          const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          if (!emiSchedule.length) return false
          const lastDate = dayjs(emiSchedule[emiSchedule.length - 1].dueDate)
          const hasDues = emiSchedule.some((e) => !isPaidEmi(e))
          return lastDate.isBefore(today) && hasDues
        })
        .map((loan) => ({ loan, emi: {} })) // Map to loan structure
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'City', value: 'city' },
      { text: 'Expiry Date (Last Due)', value: 'last_due_date' },
      { text: 'Loan Amount Sanctioned', value: 'amount_sanctioned' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'Pending Days (DPD)', value: 'pending_days' },
      { text: 'Last Payment Date', value: 'last_payment_date' },
      { text: 'Last Payment Amount', value: 'last_payment_amount' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Tenure', value: 'tenure' },
      { text: 'Interest Rate', value: 'interest_rate' },
      { text: 'Disbursal Date', value: 'disbursement_date' },
      { text: 'Balance EMIs', value: 'balance_emis' },
      { text: 'Installments Received', value: 'installments_received' },
      { text: 'Product', value: 'product' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collection Executive', value: 'collection_executive' }
    ]
  },
  'VAS Report': {
    apiType: 'loan-data',
    getRows: (loans, filters) => {
      const rows = []
      loans.forEach((loan) => {
        const charges = loan.additionalCharges || loan.loanDetails?.additionalCharges || []
        charges.forEach((charge) => {
          if (charge.isDeleted) return
          if (filters && filters.start && filters.end) {
            const cDate = dayjs(charge.dueDate || charge.startDate)
            const start = dayjs(filters.start).startOf('day')
            const end = dayjs(filters.end).endOf('day')
            if (cDate.isBefore(start) || cDate.isAfter(end)) return
          }
          rows.push({ loan, vas: charge })
        })
      })
      return rows
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'VAS Name', value: 'vas_name' },
      { text: 'Amount', value: 'vas_amount' },
      { text: 'Due Date', value: 'vas_due_date' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collection Executive', value: 'collection_executive' }
    ]
  },
  'Future payment report': {
    apiType: 'emi-details',
    getRows: (emis, filters) => {
      const today = dayjs()
      return emis
        .filter((emi) => !isPaidEmi(emi) && dayjs(emi.dueDate).isAfter(today))
        .map((emi) => ({ loan: emi.loan, emi }))
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'EMI No', value: 'emi_no' },
      { text: 'Future Due Date', value: 'due_date' },
      { text: 'Estimated EMI', value: 'emi_amount_due' },
      { text: 'Principal Amount', value: 'principal_amount' },
      { text: 'Interest Amount', value: 'interest_amount' },
      { text: 'EMI Status', value: 'emi_status' },
      { text: 'Loan Amount Sanctioned', value: 'amount_sanctioned' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collection Executive', value: 'collection_executive' }
    ]
  },
  'Loan PAR Report': {
    getRows: (loans, filters) => {
      return loans
        .filter((loan) => {
          const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return emiSchedule.some((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Loan No', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Payment Mode', value: 'payment_mode' },
      { text: 'Lead Owner', value: 'lead_owner' },
      { text: 'Executive', value: 'collection_executive' },
      { text: 'Borrower Name', value: 'customer_name' },
      { text: 'Location', value: 'centre_name' },
      { text: 'State', value: 'customer_state' },
      { text: 'Dis Date', value: 'disbursement_date' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Principal', value: 'amount_sanctioned' },
      { text: 'Loan Amount', value: 'disbursement_amount' },
      { text: 'Amount Not Due', value: 'amount_not_due' },
      { text: '1-29', value: 'par_1_29' },
      { text: '30-59', value: 'par_30_59' },
      { text: '60-89', value: 'par_60_89' },
      { text: '90-119', value: 'par_90_119' },
      { text: '120-149', value: 'par_120_149' },
      { text: '150-179', value: 'par_150_179' },
      { text: '180-365', value: 'par_180_365' },
      { text: 'Above 365', value: 'par_above_365' },
      { text: 'Arrears', value: 'arrears_amount' },
      { text: 'Total PAR', value: 'total_par' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'NPA', value: 'npa_status' },
      { text: 'Total DPD', value: 'pending_days' }
    ]
  },
  'EMI Tracker Report': {
    apiType: 'emi-details',
    getRows: (emis, filters) => {
      const rows = []
      emis.forEach((emi) => {
        const payments = emi.emiPayments || []
        if (payments.length > 0) {
          payments.forEach((payment) => {
            if (payment.isDeleted) return
            if (filters && filters.start && filters.end) {
              const pDate = dayjs(payment.date)
              const start = dayjs(filters.start).startOf('day')
              const end = dayjs(filters.end).endOf('day')
              if (pDate.isBefore(start) || pDate.isAfter(end)) return
            }
            rows.push({ loan: emi.loan, emi, payment })
          })
        } else {
          rows.push({ loan: emi.loan, emi })
        }
      })
      return rows
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'EMI No', value: 'emi_no' },
      { text: 'Due Date', value: 'due_date' },
      { text: 'EMI Status', value: 'emi_status' },
      { text: 'Principal Amount', value: 'principal_amount' },
      { text: 'Interest Amount', value: 'interest_amount' },
      { text: 'Penalty / Bounce', value: 'penalty_amount' },
      { text: 'Amount Paid', value: 'amount_collected' },
      { text: 'Payment Date', value: 'collection_date' },
      { text: 'Payment Mode', value: 'payment_mode' },
      { text: 'Receipt No', value: 'receipt_no' },
      { text: 'Loan Amount Sanctioned', value: 'amount_sanctioned' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Tenure', value: 'tenure' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Sanction Number', value: 'sanction_number' }
    ]
  },
  'Cash Inflow Report': {
    apiType: 'emi-details',
    getRows: (emis, filters) => {
      const rows = []
      emis.forEach((emi) => {
        if (!isPaidEmi(emi)) return

        const payments = emi.emiPayments || []
        if (payments.length > 0) {
          payments.forEach((payment) => {
            if (payment.isDeleted) return
            if (filters && filters.start && filters.end) {
              const pDate = dayjs(payment.date)
              const start = dayjs(filters.start).startOf('day')
              const end = dayjs(filters.end).endOf('day')
              if (pDate.isBefore(start) || pDate.isAfter(end)) return
            }
            rows.push({ loan: emi.loan, emi, payment })
          })
        } else {
          rows.push({ loan: emi.loan, emi })
        }
      })
      return rows
    },
    columns: [
      { text: 'Collection Date', value: 'collection_date' },
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'EMI No', value: 'emi_no' },
      { text: 'Due Date', value: 'due_date' },
      { text: 'EMI Status', value: 'emi_status' },
      { text: 'Principal Received', value: 'principal_amount' },
      { text: 'Interest Received', value: 'interest_amount' },
      { text: 'Late Fees / Bounce', value: 'overdue_charges' },
      { text: 'Total Received', value: 'amount_collected' },
      { text: 'Payment Mode', value: 'payment_mode' },
      { text: 'Receipt No', value: 'receipt_no' },
      { text: 'Branch', value: 'branch' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Loan Amount Sanctioned', value: 'amount_sanctioned' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' }
    ]
  },
  'Cash Outflow Report': {
    getRows: (loans, filters) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Disbursal Date', value: 'disbursal_date' },
      { text: 'Sanctioned Amount', value: 'amount_sanctioned' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' },
      { text: 'Processing Fee', value: 'processing_fee' },
      { text: 'Net Disbursed', value: 'net_disbursed' },
      { text: 'Disbursal Mode', value: 'payment_mode' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Tenure', value: 'tenure' },
      { text: 'Interest Rate', value: 'interest_rate' },
      { text: 'Branch', value: 'branch' },
      { text: 'Product', value: 'product' },
      { text: 'Collection Executive', value: 'collection_executive' }
    ]
  },
  'Business Done Report': {
    getRows: (loans, filters) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'File No.', value: 'file_no' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Company', value: 'company_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre Name', value: 'centre_name' },
      { text: 'Entity Type', value: 'ownership_type' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Loan Category', value: 'loan_category' },
      { text: 'Purpose of Loan', value: 'purpose_of_loan' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer No.', value: 'customer_no' },
      { text: 'Guarantor Name', value: 'guarantor_name' },
      { text: 'Guarantor No.', value: 'guarantor_no' },
      { text: 'Co Borrower Name', value: 'co_borrower_name' },
      { text: 'Co Borrower ContNo.', value: 'co_borrower_cont_no' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'First Due Date', value: 'first_due_date' },
      { text: 'Installments', value: 'tenure_count' },
      { text: 'Frequency', value: 'frequency' },
      { text: 'Loan Amount', value: 'amount_sanctioned' },
      { text: 'Total Amt', value: 'total_amt' },
      { text: 'First Installment', value: 'first_installment' },
      { text: 'Interest (%)', value: 'interest_rate' },
      { text: 'IndicativeROI (%)', value: 'indicative_roi' },
      { text: 'Effective APR (%)', value: 'effective_apr' },
      { text: 'IRR', value: 'irr' },
      { text: 'Deductions', value: 'deductions' },
      { text: 'Source Type', value: 'source_type' },
      { text: 'Source Name', value: 'source_name' },
      { text: 'Date Of Incorporation', value: 'date_of_incorporation' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Lead Owner', value: 'lead_owner' },
      { text: 'Renewed', value: 'renewed' },
      { text: 'Product', value: 'product' },
      { text: 'Customer State', value: 'customer_state' },
      { text: 'Area', value: 'area' },
      { text: 'Landmark', value: 'landmark' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Simple Interest', value: 'simple_interest' },
      { text: 'Dealer Name', value: 'dealer_name' },
      { text: 'Last Due Date', value: 'last_due_date' },
      { text: 'Last Installment Due Date', value: 'last_installment_due_date' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' },
      { text: 'Disbursement Date', value: 'disbursement_date' },
      { text: 'Disbursement Status', value: 'disbursement_status' },
      { text: 'Credit Score', value: 'credit_score' },
      { text: 'CKYC Id', value: 'ckyc_id' },
      { text: 'Loan Amount', value: 'amount_sanctioned_repeat' },
      { text: 'Finance Tags', value: 'finance_tags' },
      { text: 'Floating Rate', value: 'floating_rate' },
      { text: 'Loan Insurance Amount', value: 'loan_insurance_amount' },
      { text: 'Loan Insurance Tenure', value: 'loan_insurance_tenure' }
    ]
  },
  'Loan Closure Report': {
    getRows: (loans, filters) => {
      return loans
        .filter((loan) => {
          const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return emiSchedule.length > 0 && emiSchedule.every((e) => isPaidEmi(e))
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Sanction Date', value: 'sanction_date' },
      { text: 'Closure Date', value: 'last_payment_date' },
      { text: 'Sanctioned Amount', value: 'amount_sanctioned' },
      { text: 'Total Finance Amount', value: 'total_finance' },
      { text: 'Tenure', value: 'tenure' },
      { text: 'Installments', value: 'tenure_count' },
      { text: 'Interest Rate', value: 'interest_rate' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Product', value: 'product' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Branch', value: 'branch' },
      { text: 'Status', value: 'emi_status' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Guarantor Name', value: 'guarantor_name' },
      { text: 'Guarantor Mobile', value: 'guarantor_no' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' }
    ]
  },
  'CIBIL TUDF Format': {
    getRows: (loans, filters) => loans.map((loan) => ({ loan, emi: {} })),
    columns: CIBIL_HEADERS.map((h) => ({ text: h, value: h }))
  },
  'CIBIL UCRF Format': {
    getRows: (loans, filters) => loans.map((loan) => ({ loan, emi: {} })),
    columns: CIBIL_HEADERS.map((h) => ({ text: h, value: h }))
  },
  'Attendance Report': {
    apiType: 'attendance',
    columns: [
      { text: 'Attendance', value: 'Attendance' },
      { text: 'First Clock-In', value: 'First Clock-In' },
      { text: 'Last Clock-Out', value: 'Last Clock-Out' },
      { text: 'Planned Working Hour', value: 'Planned Working Hour' },
      { text: 'Completed Working Hour', value: 'Completed Working Hour' },
      { text: 'Overtime', value: 'Overtime' },
      { text: 'Clock-In Image', value: 'Clock-In Image' },
      { text: 'Clock-Out Image', value: 'Clock-Out Image' },
      { text: 'Delay In Shift', value: 'Delay In Shift' },
      { text: 'Breaks Count', value: 'Breaks Count' },
      { text: 'Distance (km)', value: 'Distance (km)' }
    ]
  },
  'Muster Report-I': {
    apiType: 'attendance',
    columns: [
      { text: 'Attendance', value: 'Attendance' },
      { text: 'First Clock-In', value: 'First Clock-In' },
      { text: 'Last Clock-Out', value: 'Last Clock-Out' },
      { text: 'Working Hours', value: 'Working Hours' },
      { text: 'Overtime', value: 'Overtime' },
      { text: 'Delay In Shift', value: 'Delay In Shift' },
      { text: 'Breaks Count', value: 'Breaks Count' },
      { text: 'Distance (km)', value: 'Distance (km)' }
    ]
  },
  'Muster Report-II': {
    apiType: 'attendance',
    categories: [
      {
        name: 'Employee Details',
        columns: [
          { text: 'Employee Name', value: 'Employee Name' },
          { text: 'Emp Id', value: 'Emp Id' },
          { text: 'Department', value: 'Department' },
          { text: 'Designation', value: 'Designation' }
        ]
      },
      {
        name: 'Manager Details',
        columns: [
          { text: 'Direct Manager', value: 'Direct Manager' },
          { text: 'Manager Hierarchy', value: 'Manager Hierarchy' }
        ]
      },
      {
        name: 'Daily Attendance Reports',
        columns: [
          { text: 'Date', value: 'Date' },
          { text: 'Attendance', value: 'Attendance' },
          { text: 'First Clock-In', value: 'First Clock-In' },
          { text: 'Last Clock-Out', value: 'Last Clock-Out' },
          { text: 'Shift Start Time', value: 'Shift Start Time' },
          { text: 'Planned Working Hour', value: 'Planned Working Hour' },
          { text: 'Completed Working Hour', value: 'Completed Working Hour' },
          { text: 'Breaks Count', value: 'Breaks Count' },
          { text: 'Overtime', value: 'Overtime' },
          { text: 'Delay In Shift', value: 'Delay In Shift' },
          { text: 'Clock-In Image', value: 'Clock-In Image' },
          { text: 'Clock-Out Image', value: 'Clock-Out Image' }
        ]
      },
      {
        name: 'Activity Details',
        columns: [
          { text: 'Activity Date', value: 'Activity Date' },
          { text: 'Activity', value: 'Activity' },
          { text: 'Activity Start Time', value: 'Activity Start Time' },
          { text: 'Activity End Time', value: 'Activity End Time' },
          { text: 'Duration of Activity', value: 'Duration of Activity' },
          { text: 'Activity Start Location', value: 'Activity Start Location' },
          { text: 'Activity End Location', value: 'Activity End Location' }
        ]
      },
      {
        name: 'Visit Details',
        columns: [
          { text: 'Visit Date', value: 'Visit Date' },
          { text: 'Customer Name', value: 'Customer Name' },
          { text: 'Arrival Time', value: 'Arrival Time' },
          { text: 'Departure Time', value: 'Departure Time' },
          { text: 'Duration of Visit', value: 'Duration of Visit' },
          { text: 'Visit Location', value: 'Visit Location' },
          { text: 'Daily Total Visits', value: 'Daily Total Visits' },
          { text: 'Distance Breakup Per Day', value: 'Distance Breakup Per Day' },
          { text: 'Total Distance Per Day', value: 'Total Distance Per Day' },
          { text: 'Mode of Transportation', value: 'Mode of Transportation' },
          { text: 'Notes/Comments', value: 'Notes/Comments' }
        ]
      },
      {
        name: 'Monthly Travel Summary',
        columns: [
          { text: 'Month', value: 'Month' },
          { text: 'Total Distance Traveled', value: 'Total Distance Traveled' },
          { text: 'Total Visits Done', value: 'Total Visits Done' }
        ]
      }
    ],
    columns: [
      'Employee Name',
      'Emp Id',
      'Department',
      'Designation',
      'City',
      'Direct Manager',
      'Manager Hierarchy',
      'Date',
      'Attendance',
      'First Clock-In',
      'Last Clock-Out',
      'Shift Start Time',
      'Planned Working Hour',
      'Completed Working Hour',
      'Breaks Count',
      'Overtime',
      'Delay In Shift',
      'Clock-In Image',
      'Clock-Out Image',
      'Activity Date',
      'Activity',
      'Activity Start Time',
      'Activity End Time',
      'Duration of Activity',
      'Activity Start Location',
      'Activity End Location',
      'Visit Date',
      'Customer Name',
      'Arrival Time',
      'Departure Time',
      'Duration of Visit',
      'Visit Location',
      'Daily Total Visits',
      'Distance Breakup Per Day',
      'Total Distance Per Day',
      'Mode of Transportation',
      'Notes/Comments',
      'Month',
      'Total Distance Traveled',
      'Total Visits Done'
    ].map((c) => ({ text: c, value: c }))
  },
  'Visit Report': {
    apiType: 'collection-tasks',
    getRows: (tasks, filters) => tasks.map((task) => ({ task, loan: task.case, emi: {} })),
    columns: [
      { text: 'Visit Title', value: 'visit_title' },
      { text: 'Case No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Visit Status', value: 'visit_status' },
      { text: 'Started At', value: 'visit_started' },
      { text: 'Completed At', value: 'visit_completed' },
      { text: 'Assigned To', value: 'visit_assigned_to' },
      { text: 'Meeting Outcome', value: 'meeting_outcome' }
    ]
  },
  'Days Past Due Report': {
    getRows: (loans, filters) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'Branch', value: 'branch' },
      { text: 'Centre', value: 'centre' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile_dpd' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Source', value: 'source' },
      { text: 'Source Name', value: 'source_name_dpd' },
      { text: 'Sub Source Name', value: 'sub_source' },
      { text: 'Lead Owner', value: 'lead_owner' },
      { text: 'Loan Amount', value: 'amount_sanctioned' },
      { text: 'Interest Type', value: 'interest_type' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' },
      { text: 'Disbursement Date', value: 'disbursement_date' },
      { text: 'First Installment Amount', value: 'first_installment_amount' },
      { text: 'Installment Start Date', value: 'installment_start_date' },
      { text: 'No. of Installments received(On Time)', value: 'installments_received_on_time' },
      { text: 'Current DPD', value: 'current_dpd' },
      { text: 'Cumulative Overdue', value: 'cumulative_overdue' },
      { text: 'Cumulative Overdue Collected', value: 'cumulative_overdue_collected' },
      { text: 'Collection Executive', value: 'collection_executive' }
    ]
  },
  'Overdue List Report': {
    getRows: (loans, filters) => {
      return loans
        .filter((loan) => {
          const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return schedule.some((e) => !isPaidEmi(e) && dayjs(e.dueDate).isBefore(dayjs()))
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Company', value: 'company_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre', value: 'centre_name' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Customer Email', value: 'customer_email' },
      { text: 'Dues', value: 'dues' },
      { text: 'Due Amount', value: 'due_amount' },
      { text: 'Pending EMI Date', value: 'pending_emi_date' },
      { text: 'Language', value: 'language' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' }
    ]
  },
  'LCC Report': {
    getRows: (loans, filters) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Company', value: 'company_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre Name', value: 'centre_name' },
      { text: 'Entity Type', value: 'ownership_type' },
      { text: 'Region', value: 'region' },
      { text: 'Type', value: 'renewed' },
      { text: 'Loan Category', value: 'loan_category' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Father/Spouse Name', value: 'father_spouse' },
      { text: 'Cust Mobile', value: 'customer_no' },
      { text: 'Alternate Contact No', value: 'alternate_no' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'Guarantor', value: 'guarantor_name' },
      { text: 'Guarantor Mobile', value: 'guarantor_no' },
      { text: 'Guarantor Address', value: 'guarantor_address' },
      { text: 'Principal', value: 'amount_sanctioned' },
      { text: 'Loan Amount', value: 'amount_sanctioned_repeat' },
      { text: 'Total Finance', value: 'total_finance' },
      { text: 'Installments', value: 'tenure_count' },
      { text: 'Interest', value: 'interest_rate' },
      { text: 'Frequency', value: 'frequency' },
      { text: 'EMI Type', value: 'emi_type' },
      { text: 'First Due Date', value: 'first_due_date' },
      { text: 'Last Due Date', value: 'last_due_date' },
      { text: 'Installment Date', value: 'pending_emi_date' },
      { text: 'Area', value: 'area' },
      { text: 'Source', value: 'source_type' },
      { text: 'Source Name', value: 'source_name' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Latest Status', value: 'latest_status' },
      { text: 'Cont. IRR (%)', value: 'irr' },
      { text: 'Arrears', value: 'arrears_amount' },
      { text: 'Arrear Received', value: 'arrear_received' },
      { text: 'Prev Adv Coll', value: 'prev_adv_coll' },
      { text: 'Month TBC', value: 'month_tbc' },
      { text: 'Month TBC Received', value: 'month_tbc_received' },
      { text: 'Total Dues', value: 'due_amount' },
      { text: 'Month Collected', value: 'month_collected' },
      { text: 'Gross TBC', value: 'gross_tbc' },
      { text: 'Gross Demand', value: 'gross_demand' },
      { text: 'Gross Collected', value: 'gross_collected' },
      { text: 'Coll/Demand (%)', value: 'coll_demand_pct' },
      { text: 'Receipt Count', value: 'receipt_no' },
      { text: 'Advance Coll.', value: 'advance_emi' },
      { text: 'EMI Due Count', value: 'dues_count' },
      { text: 'Running EMI Count', value: 'emi_no' },
      { text: 'Paid Installments', value: 'installments_received' },
      { text: 'Balance Installments', value: 'balance_emis' },
      { text: 'LPC Dues', value: 'lpc_amount' },
      { text: 'LPC Discount', value: 'overdue_charges' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'Last Rcvd Date', value: 'last_payment_date' },
      { text: 'Cumm VAS', value: 'cumm_vas' },
      { text: 'VAS Collected', value: 'vas_collected' },
      { text: 'VAS Due', value: 'vas_due' },
      { text: 'Cumm Handloan', value: 'cumm_handloan' },
      { text: 'Handloan Collected', value: 'handloan_collected' },
      { text: 'Handloan Due', value: 'handloan_due' },
      { text: 'Principal Outstanding', value: 'balance_outstanding' },
      { text: 'Principal Received', value: 'cumulative_collection' },
      { text: 'Interest Received', value: 'cumulative_collection' },
      { text: 'LPC Collected', value: 'overdue_charges' },
      { text: 'Date Of Incorporation', value: 'date_of_incorporation' },
      { text: 'Future Principal Dues', value: 'future_flow' },
      { text: 'Settlement Amount', value: 'settlement_amount' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' },
      { text: 'Total Pending Installments', value: 'dues_count' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Communication Enabled', value: 'comm_enabled' },
      { text: 'Legal', value: 'legal' },
      { text: 'Tagging Name', value: 'centre_name' },
      { text: 'Last Instalment Paid Date', value: 'last_payment_date' },
      { text: 'Last EMI Paid', value: 'last_emi_paid' },
      { text: 'Mode of RePayment', value: 'payment_mode' },
      { text: 'Funding Type', value: 'funding_type' },
      { text: 'Floating Rate', value: 'floating_rate' },
      { text: 'Interest Outstanding', value: 'overdue_interest' },
      { text: 'Asset Category', value: 'asset_category' },
      { text: 'Provisioning Amount', value: 'provisioning_amount' }
    ]
  },
  'Trial Balance Report': {
    apiType: 'trial-balance',
    getRows: (rawData, filters) => {
      const rawJournals = rawData.journals || []
      const ledgerGroups = rawData.ledgerGroups || []

      const groupSummaries = {}

      // 1. Initialize summaries from ALL ledger groups
      ledgerGroups.forEach((lg) => {
        groupSummaries[lg._id] = {
          _id: lg._id,
          name: lg.name,
          nature: lg.nature,
          category: lg.category,
          debit: 0,
          credit: 0,
          items: {}
        }
      })

      const processItem = (lgId, accId, accName, debit, credit) => {
        if (!lgId || !groupSummaries[lgId]) return

        const d = Number(debit) || 0
        const c = Number(credit) || 0

        // Sum raw values first, we will net them at the end
        groupSummaries[lgId].debit += d
        groupSummaries[lgId].credit += c

        if (!groupSummaries[lgId].items[accId]) {
          groupSummaries[lgId].items[accId] = { name: accName, debit: 0, credit: 0 }
        }
        groupSummaries[lgId].items[accId].debit += d
        groupSummaries[lgId].items[accId].credit += c
      }

      // 2. Process journals (pre-calculated by backend for aggregated-bs)
      rawJournals.forEach((j) => {
        const lgId = j.ledgerGroupId
        if (!lgId) return

        // Resolve category: prefer the groupSummaries map (from ledgerGroups list),
        // fall back to the enriched category the backend already resolved on the journal.
        const groupFromMap = groupSummaries[lgId]
        const groupCat = groupFromMap
          ? (groupFromMap.category || '').toLowerCase()
          : (j.ledgerGroupCat || '').toLowerCase()

        const isBSAccount = [
          'assets',
          'asset',
          'liabilities',
          'liability',
          'equity',
          'liabilities & equities',
          'other'
        ].includes(groupCat)

        // KEY FIX: For Balance Sheet accounts, use cumulative balance (opening + period).
        // For Income/Expense accounts, use ONLY period movements to avoid including
        // historical carry-forwards from prior financial years which inflate the values.
        const effectiveDebit = isBSAccount ? j.debit || 0 : j.periodDebit || 0
        const effectiveCredit = isBSAccount ? j.credit || 0 : j.periodCredit || 0

        // If the group is not in our map yet (edge case), initialize it
        if (!groupFromMap) {
          groupSummaries[lgId] = {
            _id: lgId,
            name: j.ledgerGroupName || 'Uncategorized',
            nature: '',
            category: j.ledgerGroupCat || '',
            debit: 0,
            credit: 0,
            items: {}
          }
        }

        processItem(
          lgId,
          j.accountsManagementId || 'unknown',
          j.accountsManagement?.name || 'Miscellaneous',
          effectiveDebit,
          effectiveCredit
        )
      })

      // 3. Convert to array, apply netting, and filter out empty groups
      return Object.values(groupSummaries)
        .map((g) => {
          const groupNet = g.debit - g.credit
          return {
            ...g,
            debit: groupNet > 0 ? groupNet : 0,
            credit: groupNet < 0 ? Math.abs(groupNet) : 0,
            items: Object.values(g.items)
              .map((i) => {
                const itemNet = i.debit - i.credit
                return {
                  ...i,
                  debit: itemNet > 0 ? itemNet : 0,
                  credit: itemNet < 0 ? Math.abs(itemNet) : 0
                }
              })
              .filter((i) => i.debit !== 0 || i.credit !== 0)
              .sort((a, b) => a.name.localeCompare(b.name))
          }
        })
        .filter((g) => g.debit !== 0 || g.credit !== 0)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    columns: [
      { text: 'Ledger Group', value: 'name' },
      { text: 'Debit (in Rs.)', value: 'debit' },
      { text: 'Credit (in Rs.)', value: 'credit' }
    ]
  },
  'Profit and Loss Report': {
    apiType: 'accounts-management',
    getRows: (rawData, filters) => {
      const rawJournals = rawData.journals || []
      const ledgerGroups = rawData.ledgerGroups || []
      const accountBalances = {}
      const groupMap = {}

      // 1. Build Group Maps from ledgerGroups
      const mongoGroupMap = {}
      const sqlGroupMap = {}

      ledgerGroups.forEach((lg) => {
        const info = {
          name: lg.name,
          category: lg.category || '',
          nature: lg.nature || '',
          sqlNatureId: lg.metaData?.sqlNatureId || null
        }
        mongoGroupMap[lg._id] = info
        if (lg.sqlLedgerGroupClientId) {
          sqlGroupMap[lg.sqlLedgerGroupClientId] = info
        }
      })

      // 2. Aggregate balances from journals
      rawJournals.forEach((item) => {
        const accId = item.accountsManagementId || 'unknown'
        if (!accountBalances[accId]) {
          const am = item.accountsManagement || {}

          // Resolve group info using Mongo ID first, then SQL ID
          let groupInfo = mongoGroupMap[item.ledgerGroupId]
          if (!groupInfo && item.sqlLedgerGroupClientId) {
            groupInfo = sqlGroupMap[item.sqlLedgerGroupClientId]
          }

          accountBalances[accId] = {
            name: am.name || 'Miscellaneous',
            groupName: groupInfo?.name || 'Other Expenses',
            groupCategory: groupInfo?.category || '',
            groupNature: groupInfo?.nature || '',
            groupSqlNatureId: groupInfo?.sqlNatureId || null,
            debit: 0,
            credit: 0
          }
        }
        accountBalances[accId].debit += Number(item.debit) || 0
        accountBalances[accId].credit += Number(item.credit) || 0
      })

      const incomeGroups = {}
      const expenseGroups = {}

      // 3. Distribute accounts into dynamic groups based on resolved category/nature/sqlNatureId
      Object.values(accountBalances).forEach((acc) => {
        const groupName = acc.groupName
        const cat = (acc.groupCategory || '').toLowerCase()
        const nature = (acc.groupNature || '').toLowerCase()
        const sqlNatureId = acc.groupSqlNatureId

        // Strictly consider only exact match for Income and Expenses
        const isIncome = cat === 'income' || cat === 'incomes'
        const isExpense = cat === 'expense' || cat === 'expenses'

        if (isIncome) {
          const bal = acc.credit - acc.debit
          if (bal === 0) return
          if (!incomeGroups[groupName]) incomeGroups[groupName] = { accounts: [], total: 0 }
          incomeGroups[groupName].accounts.push({ name: acc.name, amount: bal })
          incomeGroups[groupName].total += bal
        } else if (isExpense) {
          const bal = acc.debit - acc.credit
          if (bal === 0) return
          if (!expenseGroups[groupName]) expenseGroups[groupName] = { accounts: [], total: 0 }
          expenseGroups[groupName].accounts.push({ name: acc.name, amount: bal })
          expenseGroups[groupName].total += bal
        }
      })

      // 4. Construct side-by-side lists for the template
      const expList = []
      const incList = []

      // Expenses side
      Object.keys(expenseGroups)
        .sort()
        .forEach((gn) => {
          const group = expenseGroups[gn]
          expList.push({ particulars: gn, isSubHeader: true, amount: group.total })
          group.accounts.forEach((acc) => {
            expList.push({ particulars: acc.name, amount: acc.amount, isItem: true, groupName: gn })
          })
        })

      // Income side
      Object.keys(incomeGroups)
        .sort()
        .forEach((gn) => {
          const group = incomeGroups[gn]
          incList.push({ particulars: gn, isSubHeader: true, amount: group.total })
          group.accounts.forEach((acc) => {
            incList.push({ particulars: acc.name, amount: acc.amount, isItem: true, groupName: gn })
          })
        })

      const totalIncome = Object.values(incomeGroups).reduce((sum, g) => sum + g.total, 0)
      const totalExpense = Object.values(expenseGroups).reduce((sum, g) => sum + g.total, 0)
      const netProfit = totalIncome - totalExpense

      // Gross Profit = "Direct Income" ledger group - "Direct Expenses" ledger group
      // Match case-insensitively to handle naming variations
      const findGroupTotal = (groups, searchName) => {
        const key = Object.keys(groups).find(
          (k) => k.trim().toLowerCase() === searchName.toLowerCase()
        )
        return key ? groups[key].total : 0
      }
      const directIncomeTotal = findGroupTotal(incomeGroups, 'Direct Income')
      const directExpenseTotal = findGroupTotal(expenseGroups, 'Direct Expenses')
      const grossProfit = directIncomeTotal - directExpenseTotal

      // Add Total Expenses and Total Income rows
      expList.push({ particulars: 'Total Expenses', amount: totalExpense, isTotalRow: true })
      incList.push({ particulars: 'Total Income', amount: totalIncome, isTotalRow: true })

      // Add Gross Profit row (Direct Income ledger group - Direct Expenses ledger group)
      const grossProfitRow = {
        particulars: grossProfit >= 0 ? 'Gross Profit' : 'Gross Loss',
        amount: Math.abs(grossProfit),
        isGrossProfit: true,
        isGrossProfitPositive: grossProfit >= 0
      }
      // Put Gross Profit on the expense side if profit, income side if loss (to balance)
      if (grossProfit >= 0) {
        expList.push(grossProfitRow)
      } else {
        incList.push(grossProfitRow)
      }

      // Add Net Profit/Loss at the end
      if (netProfit >= 0) {
        expList.push({
          particulars: 'Net Profit',
          amount: netProfit,
          isMainTotal: true,
          isProfit: true
        })
      } else {
        incList.push({
          particulars: 'Net Loss',
          amount: Math.abs(netProfit),
          isMainTotal: true,
          isProfit: true
        })
      }

      // Zip into final row format
      const rows = []
      const maxLen = Math.max(expList.length, incList.length)
      const format = (val) =>
        val === undefined
          ? ''
          : Number(val).toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })

      for (let i = 0; i < maxLen; i++) {
        const exp = expList[i] || {}
        const inc = incList[i] || {}
        rows.push({
          exp_particulars: exp.particulars || '',
          exp_amount: exp.amount !== undefined ? format(exp.amount) : '',
          exp_isMainHeader: !!exp.isMainHeader,
          exp_isSubHeader: !!exp.isSubHeader,
          exp_isItem: !!exp.isItem,
          exp_isMainTotal: !!exp.isMainTotal,
          exp_isProfit: !!exp.isProfit,
          exp_isTotalRow: !!exp.isTotalRow,
          exp_isGrossProfit: !!exp.isGrossProfit,
          exp_isGrossProfitPositive: !!exp.isGrossProfitPositive,
          exp_groupName: exp.groupName || '',

          inc_particulars: inc.particulars || '',
          inc_amount: inc.amount !== undefined ? format(inc.amount) : '',
          inc_isMainHeader: !!inc.isMainHeader,
          inc_isSubHeader: !!inc.isSubHeader,
          inc_isItem: !!inc.isItem,
          inc_isMainTotal: !!inc.isMainTotal,
          inc_isProfit: !!inc.isProfit,
          inc_isTotalRow: !!inc.isTotalRow,
          inc_isGrossProfit: !!inc.isGrossProfit,
          inc_isGrossProfitPositive: !!inc.isGrossProfitPositive,
          inc_groupName: inc.groupName || ''
        })
      }

      return rows
    },
    columns: [
      { text: 'Section', value: 'pnl_category' },
      { text: 'Particulars', value: 'particulars' },
      { text: 'Income (Cr)', value: 'income' },
      { text: 'Expense (Dr)', value: 'expense' }
    ]
  },
  'Balance Sheet Report': {
    apiType: 'accounts-management',
    getRows: (rawData, filters) => {
      const ledgerGroups = rawData.ledgerGroups || []
      const rawJournals = rawData.journals || []

      // 1. Build group lookup maps from ledgerGroups list
      const groupMap = {} // by mongo _id (string)
      const sqlGroupMap = {} // by sqlLedgerGroupClientId (string)

      ledgerGroups.forEach((lg) => {
        const cat = (lg.category || lg.nature || '').toUpperCase()
        const isLiability = ['LIABILITIES', 'EQUITY', 'LIABILITY'].includes(cat)
        const isOther = (lg.category || '').toLowerCase() === 'other'
        const info = {
          name: lg.name,
          cat: lg.category || lg.nature || '',
          // 'OTHER' = Profit And Loss group shown on liabilities side
          side: isLiability ? 'LIABILITIES' : isOther ? 'OTHER' : 'ASSETS'
        }
        groupMap[String(lg._id)] = info
        if (lg.sqlLedgerGroupClientId) {
          sqlGroupMap[String(lg.sqlLedgerGroupClientId)] = info
        }
      })

      // 2. Aggregate account balances
      // Journals from /aggregated-bs endpoint come with finalLedgerGroup pre-populated
      const accountBalances = {}

      rawJournals.forEach((j) => {
        const accId = String(j.accountsManagementId || j.sqlChartOfAccountId || 'unknown')
        if (!accountBalances[accId]) {
          // Priority: finalLedgerGroup (from backend enrichment) → mongo map → sql map
          const finalLg = j.finalLedgerGroup || null
          let groupInfo = finalLg
            ? {
                name: finalLg.name,
                cat: finalLg.category || finalLg.nature || '',
                side: ['LIABILITIES', 'EQUITY', 'LIABILITY'].includes(
                  (finalLg.category || finalLg.nature || '').toUpperCase()
                )
                  ? 'LIABILITIES'
                  : (finalLg.category || '').toLowerCase() === 'other'
                  ? 'OTHER'
                  : 'ASSETS'
              }
            : groupMap[String(j.ledgerGroupId)] ||
              sqlGroupMap[String(j.sqlLedgerGroupClientId)] ||
              null

          accountBalances[accId] = {
            name: j.accountsManagement?.name || 'Miscellaneous',
            // FIX: Use LEDGER GROUP category as the classification key — not the account's own
            // category. This ensures Profit And Loss accounts (cat=Other at group level)
            // are included even if individual accounts have cat=Income/Expense.
            ledgerGroupCat: groupInfo?.cat || '',
            ledgerGroupName: groupInfo?.name || 'Uncategorized',
            ledgerGroupSide: groupInfo?.side || 'ASSETS',
            ledgerGroupId: String(j.ledgerGroupId || ''),
            sqlLedgerGroupClientId: String(j.sqlLedgerGroupClientId || ''),
            debit: 0,
            credit: 0,
            periodDebit: 0,
            periodCredit: 0
          }
        }
        accountBalances[accId].debit += Number(j.debit) || 0
        accountBalances[accId].credit += Number(j.credit) || 0
        accountBalances[accId].periodDebit += Number(j.periodDebit) || 0
        accountBalances[accId].periodCredit += Number(j.periodCredit) || 0
      })

      // 3. Bucket accounts into sections by ledger group
      // Sections: LIABILITIES, ASSETS, OTHER (Profit And Loss)
      const sectionBuckets = {}
      const balanceSheetCats = ['assets', 'asset', 'liabilities', 'liability', 'equity']
      let currentYearProfit = 0
      let accumulatedProfit = 0

      Object.values(accountBalances).forEach((acc) => {
        const groupCat = (acc.ledgerGroupCat || '').toLowerCase()
        const isBS = balanceSheetCats.includes(groupCat)
        const isOther = groupCat === 'other'

        if (isBS || isOther) {
          const section = acc.ledgerGroupName
          if (!sectionBuckets[section]) {
            sectionBuckets[section] = {
              accounts: [],
              naturalSide: isOther ? 'OTHER' : acc.ledgerGroupSide
            }
          }
          sectionBuckets[section].accounts.push(acc)
        } else {
          // Everything else is Income/Expense
          // 1. Current Year Performance (Current FY movements)
          currentYearProfit += acc.periodCredit - acc.periodDebit
          // 2. Accumulated Performance (Historical balances carried forward)
          accumulatedProfit += acc.openingCredit - acc.openingDebit
        }
      })

      // 4. Add Profit components to the "Profit And Loss" (OTHER) section
      if (currentYearProfit !== 0 || accumulatedProfit !== 0) {
        let plSectionName = 'Profit And Loss'
        const otherSection = Object.keys(sectionBuckets).find(
          (s) => sectionBuckets[s].naturalSide === 'OTHER'
        )
        if (otherSection) plSectionName = otherSection

        if (!sectionBuckets[plSectionName]) {
          sectionBuckets[plSectionName] = {
            accounts: [],
            naturalSide: 'OTHER'
          }
        }

        // Add Opening Balance (Accumulated profit from previous years)
        if (accumulatedProfit !== 0) {
          sectionBuckets[plSectionName].accounts.push({
            name: 'Opening Profit/Loss',
            debit: accumulatedProfit < 0 ? Math.abs(accumulatedProfit) : 0,
            credit: accumulatedProfit > 0 ? accumulatedProfit : 0,
            ledgerGroupCat: 'other',
            ledgerGroupName: plSectionName,
            ledgerGroupSide: 'OTHER',
            isProfit: true
          })
        }

        // Add Current Period Profit
        if (currentYearProfit !== 0) {
          sectionBuckets[plSectionName].accounts.push({
            name: 'Current Year Profit',
            debit: currentYearProfit < 0 ? Math.abs(currentYearProfit) : 0,
            credit: currentYearProfit > 0 ? currentYearProfit : 0,
            ledgerGroupCat: 'other',
            ledgerGroupName: plSectionName,
            ledgerGroupSide: 'OTHER',
            isProfit: true
          })
        }
      }

      // 5. Determine final side for each group — at GROUP LEVEL (not per-account)

      //    Rule: If a group's total net balance is negative for its natural side,
      //          move the ENTIRE group to the opposite side (never show on both sides).
      //    Example: Assets group with (debit - credit) < 0 → show on Liabilities side
      //             Liabilities group with (credit - debit) < 0 → show on Assets side
      const sides = { LIABILITIES: {}, ASSETS: {}, OTHER: {} }

      Object.keys(sectionBuckets).forEach((section) => {
        const bucket = sectionBuckets[section]
        const naturalSide = bucket.naturalSide

        // Compute total net for the whole group based on its natural side
        let groupNet = 0
        bucket.accounts.forEach((acc) => {
          if (naturalSide === 'ASSETS') {
            groupNet += acc.debit - acc.credit
          } else if (naturalSide === 'LIABILITIES') {
            groupNet += acc.credit - acc.debit
          } else {
            // OTHER (Profit And Loss): credit - debit = profit
            groupNet += acc.credit - acc.debit
          }
        })

        // FIX: Decide one side for the entire group based on net
        let finalSide = naturalSide
        if (groupNet < 0) {
          if (naturalSide === 'ASSETS') finalSide = 'LIABILITIES'
          else if (naturalSide === 'LIABILITIES') finalSide = 'ASSETS'
          else if (naturalSide === 'OTHER') finalSide = 'ASSETS' // Loss moves to Assets
        }

        if (!sides[finalSide][section]) sides[finalSide][section] = []

        bucket.accounts.forEach((acc) => {
          let amount
          if (finalSide === 'ASSETS') {
            amount = acc.debit - acc.credit
          } else if (finalSide === 'LIABILITIES') {
            amount = acc.credit - acc.debit
          } else {
            // OTHER: credit - debit (profit = credit balance)
            amount = acc.credit - acc.debit
          }
          if (amount !== 0) {
            sides[finalSide][section].push({
              name: acc.name,
              amount: amount,
              isProfit: !!acc.isProfit
            })
          }
        })

        // Remove empty sections
        if (sides[finalSide][section].length === 0) delete sides[finalSide][section]
      })

      // 5. Build display lists
      const formatAmt = (val) =>
        val === undefined || val === null
          ? ''
          : Number(val).toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })

      const leList = [{ particulars: 'Liabilities & Equities', isMainHeader: true }]
      const assetsList = [{ particulars: 'Assets', isMainHeader: true }]

      let totalL = 0
      let totalA = 0

      // Process Liabilities
      Object.keys(sides.LIABILITIES)
        .sort()
        .forEach((gn) => {
          const items = sides.LIABILITIES[gn]
          const gTotal = items.reduce((sum, it) => sum + it.amount, 0)
          leList.push({ particulars: gn, isSubHeader: true })
          items.forEach((it) => {
            leList.push({ particulars: it.name, amount: it.amount, isItem: true, groupName: gn })
          })
          leList.push({ particulars: `Total ${gn}`, amount: gTotal, isSectionTotal: true })
          totalL += gTotal
        })

      // Process Profit And Loss group (OTHER category) — shown as-is on Liabilities side
      Object.keys(sides.OTHER)
        .sort()
        .forEach((gn) => {
          const items = sides.OTHER[gn]
          const gTotal = items.reduce((sum, it) => sum + it.amount, 0)
          leList.push({ particulars: gn, isSubHeader: true })
          items.forEach((it) => {
            leList.push({
              particulars: it.name,
              amount: it.amount,
              isItem: true,
              isProfit: true,
              groupName: gn
            })
          })
          leList.push({
            particulars: `Total ${gn}`,
            amount: gTotal,
            isSectionTotal: true,
            isProfit: true
          })
          totalL += gTotal
        })

      // Process Assets
      Object.keys(sides.ASSETS)
        .sort()
        .forEach((gn) => {
          const items = sides.ASSETS[gn]
          const gTotal = items.reduce((sum, it) => sum + it.amount, 0)
          assetsList.push({ particulars: gn, isSubHeader: true })
          items.forEach((it) => {
            assetsList.push({
              particulars: it.name,
              amount: it.amount,
              isItem: true,
              isProfit: !!it.isProfit,
              groupName: gn
            })
          })
          assetsList.push({ particulars: `Total ${gn}`, amount: gTotal, isSectionTotal: true })
          totalA += gTotal
        })

      // 6. Zip into rows
      const rows = []
      const maxLen = Math.max(leList.length, assetsList.length)

      for (let i = 0; i < maxLen; i++) {
        const le = leList[i] || {}
        const as = assetsList[i] || {}
        rows.push({
          le_particulars: le.particulars || '',
          le_amount: le.amount !== undefined ? formatAmt(le.amount) : '',
          le_isMainHeader: !!le.isMainHeader,
          le_isSubHeader: !!le.isSubHeader,
          le_isItem: !!le.isItem,
          le_isSectionTotal: !!le.isSectionTotal,
          le_isProfit: !!le.isProfit,
          le_groupName: le.groupName || '',
          assets_particulars: as.particulars || '',
          assets_amount: as.amount !== undefined ? formatAmt(as.amount) : '',
          as_isMainHeader: !!as.isMainHeader,
          as_isSubHeader: !!as.isSubHeader,
          as_isItem: !!as.isItem,
          as_isSectionTotal: !!as.isSectionTotal,
          as_groupName: as.groupName || ''
        })
      }

      rows.push({
        le_particulars: 'TOTAL LIABILITIES & EQUITIES',
        le_amount: formatAmt(totalL),
        isGrandTotal: true,
        assets_particulars: 'TOTAL ASSETS',
        assets_amount: formatAmt(totalA)
      })

      return rows
    },
    columns: [
      { text: 'Particulars (L&E)', value: 'le_particulars' },
      { text: 'Amount (Cr)', value: 'le_amount' },
      { text: 'Particulars (Assets)', value: 'assets_particulars' },
      { text: 'Amount (Dr)', value: 'assets_amount' }
    ]
  },
  'Vertical Balance Sheet Report': {
    apiType: 'accounts-management',
    getRows: (rawData, filters) => {
      const ledgerGroups = rawData.ledgerGroups || []
      const rawJournals = rawData.journals || []

      // 1. Build group lookup maps
      const groupMap = {}
      const sqlGroupMap = {}

      ledgerGroups.forEach((lg) => {
        const cat = (lg.category || lg.nature || '').toUpperCase()
        const isLiability = ['LIABILITIES', 'EQUITY', 'LIABILITY'].includes(cat)
        const isOther = (lg.category || '').toLowerCase() === 'other'
        const info = {
          name: lg.name,
          cat: lg.category || lg.nature || '',
          side: isLiability ? 'LIABILITIES' : isOther ? 'OTHER' : 'ASSETS'
        }
        groupMap[String(lg._id)] = info
        if (lg.sqlLedgerGroupClientId) {
          sqlGroupMap[String(lg.sqlLedgerGroupClientId)] = info
        }
      })

      // 2. Aggregate account balances
      const accountBalances = {}

      rawJournals.forEach((j) => {
        const accId = String(j.accountsManagementId || j.sqlChartOfAccountId || 'unknown')
        if (!accountBalances[accId]) {
          const finalLg = j.finalLedgerGroup || null
          let groupInfo = finalLg
            ? {
                name: finalLg.name,
                cat: finalLg.category || finalLg.nature || '',
                side: ['LIABILITIES', 'EQUITY', 'LIABILITY'].includes(
                  (finalLg.category || finalLg.nature || '').toUpperCase()
                )
                  ? 'LIABILITIES'
                  : (finalLg.category || '').toLowerCase() === 'other'
                  ? 'OTHER'
                  : 'ASSETS'
              }
            : groupMap[String(j.ledgerGroupId)] ||
              sqlGroupMap[String(j.sqlLedgerGroupClientId)] ||
              null

          accountBalances[accId] = {
            name: j.accountsManagement?.name || 'Miscellaneous',
            // FIX: Use ledger group category as the authoritative classification source
            ledgerGroupCat: groupInfo?.cat || '',
            ledgerGroupName: groupInfo?.name || 'Uncategorized',
            ledgerGroupSide: groupInfo?.side || 'ASSETS',
            ledgerGroupId: String(j.ledgerGroupId || ''),
            sqlLedgerGroupClientId: String(j.sqlLedgerGroupClientId || ''),
            debit: 0,
            credit: 0,
            periodDebit: 0,
            periodCredit: 0
          }
        }
        accountBalances[accId].debit += Number(j.debit) || 0
        accountBalances[accId].credit += Number(j.credit) || 0
        accountBalances[accId].periodDebit += Number(j.periodDebit) || 0
        accountBalances[accId].periodCredit += Number(j.periodCredit) || 0
      })

      // 3. Bucket accounts into sections by ledger group
      const sectionBuckets = {}
      const balanceSheetCats = ['assets', 'asset', 'liabilities', 'liability', 'equity']
      let currentYearProfit = 0

      Object.values(accountBalances).forEach((acc) => {
        const groupCat = (acc.ledgerGroupCat || '').toLowerCase()
        const isBS = balanceSheetCats.includes(groupCat)
        const isOther = groupCat === 'other'

        if (isBS || isOther) {
          const section = acc.ledgerGroupName
          if (!sectionBuckets[section]) {
            sectionBuckets[section] = {
              accounts: [],
              naturalSide: isOther ? 'OTHER' : acc.ledgerGroupSide
            }
          }
          sectionBuckets[section].accounts.push(acc)
        } else {
          // Everything else is Income/Expense -> calculate current year movement
          currentYearProfit += acc.periodCredit - acc.periodDebit
        }
      })

      // 4. Add "Current Year Profit" to the "Profit And Loss" (OTHER) section
      if (currentYearProfit !== 0) {
        let plSectionName = 'Profit And Loss'
        const otherSection = Object.keys(sectionBuckets).find(
          (s) => sectionBuckets[s].naturalSide === 'OTHER'
        )
        if (otherSection) plSectionName = otherSection

        if (!sectionBuckets[plSectionName]) {
          sectionBuckets[plSectionName] = {
            accounts: [],
            naturalSide: 'OTHER'
          }
        }
        sectionBuckets[plSectionName].accounts.push({
          name: 'Current Year Profit',
          debit: currentYearProfit < 0 ? Math.abs(currentYearProfit) : 0,
          credit: currentYearProfit > 0 ? currentYearProfit : 0,
          ledgerGroupCat: 'other',
          ledgerGroupName: plSectionName,
          ledgerGroupSide: 'OTHER',
          isProfit: true // Flag for UI styling
        })
      }

      // 5. Determine final side at GROUP level (not per-account)

      //    If a group's total net balance is negative → flip to opposite side
      const sides = { LIABILITIES: {}, ASSETS: {}, OTHER: {} }

      Object.keys(sectionBuckets).forEach((section) => {
        const bucket = sectionBuckets[section]
        const naturalSide = bucket.naturalSide

        // Compute group net
        let groupNet = 0
        bucket.accounts.forEach((acc) => {
          if (naturalSide === 'ASSETS') {
            groupNet += acc.debit - acc.credit
          } else if (naturalSide === 'LIABILITIES') {
            groupNet += acc.credit - acc.debit
          } else {
            groupNet += acc.credit - acc.debit
          }
        })

        // FIX: Decide one side for the entire group
        let finalSide = naturalSide
        if (naturalSide !== 'OTHER' && groupNet < 0) {
          finalSide = naturalSide === 'ASSETS' ? 'LIABILITIES' : 'ASSETS'
        }

        if (!sides[finalSide][section]) sides[finalSide][section] = []

        bucket.accounts.forEach((acc) => {
          let amount
          if (finalSide === 'ASSETS') {
            amount = acc.debit - acc.credit
          } else if (finalSide === 'LIABILITIES') {
            amount = acc.credit - acc.debit
          } else {
            amount = acc.credit - acc.debit
          }
          if (amount !== 0) {
            sides[finalSide][section].push({
              name: acc.name,
              amount: amount,
              isProfit: !!acc.isProfit
            })
          }
        })

        if (sides[finalSide][section].length === 0) delete sides[finalSide][section]
      })

      const rows = []
      const formatAmt = (val) =>
        val === undefined || val === null
          ? ''
          : Number(val).toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })

      // ---------------- ASSETS ----------------
      rows.push({ vbs_account: 'Assets', isMainHeader: true })
      let totalAssets = 0
      Object.keys(sides.ASSETS)
        .sort()
        .forEach((gn) => {
          const items = sides.ASSETS[gn]
          const gTotal = items.reduce((sum, it) => sum + it.amount, 0)
          rows.push({ vbs_account: gn, isSubHeader: true })
          items.forEach((it) => {
            rows.push({
              vbs_account: it.name,
              vbs_code: '',
              vbs_total: formatAmt(it.amount),
              isItem: true,
              groupName: gn
            })
          })
          rows.push({
            vbs_account: `Total ${gn}`,
            vbs_total: formatAmt(gTotal),
            isSectionTotal: true
          })
          totalAssets += gTotal
        })
      rows.push({
        vbs_account: 'Total Assets',
        vbs_total: formatAmt(totalAssets),
        isMainTotal: true
      })
      rows.push({ vbs_account: '', isSpacer: true })

      // ---------------- LIABILITIES ----------------
      rows.push({ vbs_account: 'Liabilities', isMainHeader: true })
      let totalL = 0

      Object.keys(sides.LIABILITIES)
        .sort()
        .forEach((gn) => {
          const items = sides.LIABILITIES[gn]
          const gTotal = items.reduce((sum, it) => sum + it.amount, 0)
          rows.push({ vbs_account: gn, isSubHeader: true })
          items.forEach((it) => {
            rows.push({
              vbs_account: it.name,
              vbs_code: '',
              vbs_total: formatAmt(it.amount),
              isItem: true,
              groupName: gn
            })
          })
          rows.push({
            vbs_account: `Total ${gn}`,
            vbs_total: formatAmt(gTotal),
            isSectionTotal: true
          })
          totalL += gTotal
        })

      // Profit And Loss (OTHER) — from journal data, on Liabilities side
      Object.keys(sides.OTHER)
        .sort()
        .forEach((gn) => {
          const items = sides.OTHER[gn]
          const gTotal = items.reduce((sum, it) => sum + it.amount, 0)
          rows.push({ vbs_account: gn, isSubHeader: true })
          items.forEach((it) => {
            rows.push({
              vbs_account: it.name,
              vbs_code: '',
              vbs_total: formatAmt(it.amount),
              isItem: true,
              isProfit: true,
              groupName: gn
            })
          })
          rows.push({
            vbs_account: `Total ${gn}`,
            vbs_total: formatAmt(gTotal),
            isSectionTotal: true,
            isProfit: true
          })
          totalL += gTotal
        })

      rows.push({
        vbs_account: 'Total Liabilities',
        vbs_total: formatAmt(totalL),
        isMainTotal: true
      })
      return rows
    },
    columns: [
      { text: 'ACCOUNT', value: 'vbs_account' },
      { text: 'ACCOUNT CODE', value: 'vbs_code' },
      { text: 'TOTAL', value: 'vbs_total' }
    ]
  }
}
